/**
 * e-MSS API Client
 * HTTP client para o BackEnd Python (FastAPI / Oracle VM).
 *
 * Em desenvolvimento, o Vite proxy repassa /api/* → localhost:8000/*
 * Em produção (Vercel), VITE_API_URL = https://api.emsseries.app
 *
 * Auth/Realtime continuam no Supabase (supabase.ts).
 * Este cliente cuida de operações privilegiadas que precisam do BackEnd:
 *   - Rating engine (lançamento de resultados)
 *   - Validações de ownership complexas
 *   - Operações que bypassam RLS via service_role
 */

import { supabase } from './supabase';
import toast from 'react-hot-toast';

// Em dev: usa proxy Vite (/api/*) sem CORS — em prod: URL direta
export const BASE =
  import.meta.env.MODE === 'development'
    ? '/api'
    : (import.meta.env.VITE_API_URL ?? 'https://api.emsseries.app');

// URL absoluta (pula o proxy) — usada para OAuth redirects
export const ABS_BASE =
  import.meta.env.MODE === 'development'
    ? (import.meta.env.VITE_API_URL ?? 'http://localhost:8000')
    : 'https://api.emsseries.app';

// ── Helper de extração de mensagens de erro ─────────────────────

function extractErrorMessage(obj: any): string {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj);

  if (Array.isArray(obj)) {
    return obj.map(extractErrorMessage).filter(Boolean).join('; ');
  }

  if (typeof obj === 'object') {
    if (typeof obj.message === 'string' && obj.message) return obj.message;
    if (typeof obj.detail === 'string' && obj.detail) return obj.detail;
    if (typeof obj.details === 'string' && obj.details) return obj.details;
    if (typeof obj.msg === 'string' && obj.msg) return obj.msg;
    if (typeof obj.error === 'string' && obj.error) return obj.error;

    if (obj.message) {
      const nested = extractErrorMessage(obj.message);
      if (nested) return nested;
    }
    if (obj.detail) {
      const nested = extractErrorMessage(obj.detail);
      if (nested) return nested;
    }
    if (obj.details) {
      const nested = extractErrorMessage(obj.details);
      if (nested) return nested;
    }
    if (obj.error) {
      const nested = extractErrorMessage(obj.error);
      if (nested) return nested;
    }

    try {
      return JSON.stringify(obj);
    } catch {
      return String(obj);
    }
  }
  return String(obj);
}

export class ApiError extends Error {
  public rawDetail: any;
  public detail: string;

  constructor(public status: number, detailInput: any) {
    const msg = extractErrorMessage(detailInput) || `HTTP ${status}`;
    super(msg);
    this.name = 'ApiError';
    this.detail = msg;
    this.rawDetail = detailInput;
  }
}

// ── Request Tracker (for global loading) ──────────────────────
// Apenas requisições explícitas de auth (login, logout, link/unlink) disparam o overlay.
// Chamadas normais de dados (TanStack Query) passam silent=true e não bloqueiam a UI.
let activeRequests = 0;
const requestListeners: ((loading: boolean) => void)[] = [];

export function onLoadingChange(cb: (loading: boolean) => void) {
  requestListeners.push(cb);
  return () => {
    const idx = requestListeners.indexOf(cb);
    if (idx !== -1) requestListeners.splice(idx, 1);
  };
}

function updateLoading(delta: number) {
  activeRequests = Math.max(0, activeRequests + delta);
  requestListeners.forEach(cb => cb(activeRequests > 0));
}

// ── Fetch Wrapper com Auth Header + Auto Refresh ──────────────

async function apiFetch<T>(
  path: string,
  options: RequestInit & { _silent?: boolean; silent?: boolean } = {},
  retries = 3,
  _isRetry = false
): Promise<T> {
  const silent = options._silent ?? options.silent ?? false;
  delete options._silent;
  delete options.silent;

  if (!_isRetry && !silent) updateLoading(1);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);

  try {
    const isFormData = options.body instanceof FormData;
    const headers: Record<string, string> = {
      ...(!isFormData && { 'Content-Type': 'application/json' }),
      ...((options.headers as Record<string, string>) || {}),
    };

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const url = (path.startsWith('http://') || path.startsWith('https://'))
      ? path
      : `${BASE}${path}`;

    const res = await fetch(url, { 
      ...options, 
      headers,
      credentials: 'include',
      signal: controller.signal
    });
    clearTimeout(timeoutId);

    if (!res.ok) {
      // Fallback: se o proxy /api retornar 404 em ambiente dev, tenta direto na ABS_BASE (backend 8000)
      if (res.status === 404 && !path.startsWith('http') && import.meta.env.MODE === 'development' && ABS_BASE) {
        try {
          const fallbackUrl = `${ABS_BASE}${path}`;
          const fallbackRes = await fetch(fallbackUrl, { ...options, headers, credentials: 'include' });
          if (fallbackRes.ok) {
            if (fallbackRes.status === 204) return undefined as unknown as T;
            return await fallbackRes.json();
          }
        } catch { /* ignora erro de fallback */ }
      }

      if (res.status === 503 && retries > 0) {
        console.warn(`[API] 503 em ${path}, tentando novamente (${retries} restantes)...`);
        toast.loading('Servidor ocupado. Restaurando conexão...', { id: 'api-retry', duration: 3000 });
        await new Promise(r => setTimeout(r, 2000));
        return await apiFetch<T>(path, options, retries - 1, true);
      }

      if (res.status === 401 && !(options as any)._retry && path !== '/auth/refresh' && path !== '/auth/signout') {
        (options as any)._retry = true;
        try {
          const refreshRes = await fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });
          if (refreshRes.ok) {
            return await apiFetch<T>(path, options, retries, true);
          }
        } catch (e) {}
      }

      let detail: any = `HTTP ${res.status}`;
      try {
        const json = await res.json();
        detail = json.detail ?? json.message ?? json.error ?? detail;
      } catch { /* noop */ }
      throw new ApiError(res.status, detail);
    }
    
    if (res.status === 204) return undefined as unknown as T;
    return await res.json();
  } catch (err: any) {
    clearTimeout(timeoutId);

    // Retry em erros de rede (CORS bloqueado / servidor offline)
    // Limitado a 1 tentativa para não prolongar o loading overlay
    if (err.name === 'TypeError' && retries > 1) {
      console.warn(`[API] Erro de rede em ${path}, tentando novamente (${retries} restantes)...`);
      toast.loading('Verificando conexão com o servidor...', { id: 'api-retry', duration: 3000 });
      await new Promise(r => setTimeout(r, 3000));
      return await apiFetch<T>(path, options, 1, true); // Força max 1 retry adicional
    }

    if (err.name === 'AbortError') {
      throw new ApiError(408, 'Conexão com o servidor expirou (Timeout)');
    }
    throw err;
  } finally {
    if (!_isRetry && !silent) updateLoading(-1);
  }
}


const api = {
  // silent=true → não dispara LoadingOverlay global (usado pelo TanStack Query)
  get:    <T>(p: string, silent = true)              => apiFetch<T>(p, { method: 'GET', silent }),
  post:   <T>(p: string, b?: any, silent = false)    => apiFetch<T>(p, { method: 'POST',  body: b instanceof FormData ? b : JSON.stringify(b), silent }),
  put:    <T>(p: string, b?: any, silent = false)    => apiFetch<T>(p, { method: 'PUT',   body: b instanceof FormData ? b : JSON.stringify(b), silent }),
  patch:  <T>(p: string, b?: any, silent = false)    => apiFetch<T>(p, { method: 'PATCH', body: b instanceof FormData ? b : JSON.stringify(b), silent }),
  delete: <T>(p: string, silent = false)             => apiFetch<T>(p, { method: 'DELETE', silent }),
};

export default api;

// ── Interfaces ────────────────────────────────────────────────

export interface UserHistoryItem {
  id?: string;
  session_id?: string;
  event_id?: string;
  league_slug?: string;
  scheduled_at: string;
  event_name: string;
  circuit_name: string;
  session_type: string;
  position: number;
  rating_delta: number;
  /** Delta real de mRCI calculado pela view (considera penal/FRR) */
  mrci_delta?: number;
  /** @deprecated use mrci_delta */
  rci_delta?: number;
  penal?: number | null;
  status?: string;
  sof?: number;
  commission_settings?: any;
  commission_enabled?: boolean;
  timezone?: string;
}

export interface UserStats {
  current_tier: string;
  current_ers: number;
  career_global_points: number;
  global_points_rank?: number;
  ers_rank?: number;
  rci_index?: number;
  wins?: number;
  podiums?: number;
  top_10s?: number;
  total_sessions?: number;
  total_races?: number; // Alias
  avg_position?: number;
  last_ers_delta?: number;
  rating_delta?: number;
  game_id?: string;
  game_name?: string;
  _no_data?: boolean; // Sentinel: piloto existe mas sem dados de ranking
}

export interface CategoryStats extends UserStats {
  category_slug: string;
}

// ── Auth ─────────────────────────────────────────────────────

export const authApi = {
  syncProfile:      ()                  => api.post('/auth/sync-profile'),
  signOut:          ()                  => api.post('/auth/signout'),
  discordAuthorize: ()                  => api.get<{ url: string }>('/auth/discord'),
  discordUnlink:    ()                  => api.delete('/auth/discord'),
  steamAuthorize:   ()                  => api.get<{ url: string }>('/auth/steam'),
  steamUnlink:      ()                  => api.delete('/auth/steam'),
  xboxLink:         (gamertag: string)  => api.post<{ status: string; xbox_id: string; xbox_gamertag: string; avatar?: string }>('/auth/xbox/link', { gamertag }),
  xboxUnlink:       ()                  => api.delete('/auth/xbox'),
};

export interface AdminStats {
  total_users: number;
  total_events: number;
  total_leagues: number;
  total_sessions: number;
  active_sessions: number;
  growth?: {
    date: string;
    users: number;
    sessions: number;
  }[];
}

export interface AdminUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url?: string;
  role: string;
  is_active: boolean;
  is_banned: boolean;
  last_login?: string;
  current_ers?: number;
  created_at?: string;
}

export interface Circuit {
  id: string;
  name: string;
  game_id: string;
  location?: string;
  image_url?: string;
  is_active: boolean;
}

export interface Car {
  id: string;
  manufacturer: string;
  model: string;
  game_id: string;
  category_id?: string;
  year: number;
  car_class?: string;
  image_url?: string;
  brand_logo_url?: string;
  is_active: boolean;
}

export interface PlatformCategory {
  id: string;
  name: string;
  slug: string;
  parent_id?: string | null;
}

export interface AdminMetadata {
  games: any[];
  categories: PlatformCategory[];
  license_tiers: any[];
}

export interface AdminLeagueHealth {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  owner_name: string;
  events_count: number;
  members_count: number;
  is_public: boolean;
  is_active: boolean;
  created_at: string;
}

export interface PrizeSplit {
  prize_percentage: number;
  host_percentage: number;
  platform_percentage: number;
  cutoff_percentage: number;
  decay_ratio: number;
}

export const adminApi = {
  getStats:      ()                         => api.get<AdminStats>('/admin/stats'),
  getMetadata:   ()                         => api.get<AdminMetadata>('/admin/metadata'),
  getUsers:      (limit=50, offset=0, search?: string)       => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (search) params.append('search', search);
    return api.get<{ users: AdminUser[], total: number }>(`/admin/users?${params.toString()}`);
  },
  getCircuits:   ()                         => api.get<Circuit[]>('/admin/circuits'),
  getCars:       (gameId?: string, search?: string) => {
    const params = new URLSearchParams();
    if (gameId) params.append('game_id', gameId);
    if (search) params.append('search', search);
    return api.get<Car[]>(`/admin/cars${params.toString() ? '?' + params.toString() : ''}`);
  },
  getEvents:     ()                         => api.get<any[]>('/admin/events'),
  getEventsSchedule: (weekStart: string, weekEnd: string) =>
    api.get<any[]>(`/admin/events-schedule?week_start=${weekStart}&week_end=${weekEnd}`),
  getLeaguesHealth: ()                      => api.get<AdminLeagueHealth[]>('/admin/leagues/health'),
  banUser:       (id: string)               => api.post(`/admin/users/${id}/ban`),
  unbanUser:     (id: string)               => api.post(`/admin/users/${id}/unban`),
  adjustErs: (pilotId: string, delta: number, reason: string) => 
    api.post('/admin/ers-adjust', { pilot_id: pilotId, delta, reason }),
  getRoles:      ()                         => api.get<any[]>('/admin/roles'),
  getUserRoles:  (id: string)               => api.get<any[]>(`/admin/users/${id}/roles`),
  assignRole:   (userId: string, roleId: string | number, tier: 'free' | 'paid' = 'free') => 
    api.post(`/admin/users/${userId}/roles`, { role_id: roleId, subscription_tier: tier }),
  revokeRole:   (userId: string, roleId: string | number) => 
    api.delete(`/admin/users/${userId}/roles/${roleId}`),
  createCar:     (d: object)                => api.post('/admin/cars', d),
  updateCar:     (id: string, d: object)    => api.patch(`/admin/cars/${id}`, d),
  deleteCar:     (id: string)               => api.delete(`/admin/cars/${id}`),
  createCircuit: (d: object)                => api.post('/admin/circuits', d),
  updateCircuit: (id: string, d: object)    => api.patch(`/admin/circuits/${id}`, d),
  deleteCircuit: (id: string)               => api.delete(`/admin/circuits/${id}`),
  createLayout:  (d: object)                => api.post('/admin/circuit-layouts', d),
  updateLayout:  (id: string, d: object)    => api.patch(`/admin/circuit-layouts/${id}`, d),
  deleteLayout:  (id: string)               => api.delete(`/admin/circuit-layouts/${id}`),

  updateEvent:  (id: string, d: object) => api.patch(`/admin/events/${id}`, d),
  getPrizeSplit: () => api.get<PrizeSplit>('/admin/prize-split'),
  updatePrizeSplit: (d: PrizeSplit) =>
    api.patch<{ settings: PrizeSplit; events_resynced: number }>('/admin/prize-split', d),
  getLicenseTiers: () => api.get<any[]>('/admin/license-tiers'),
  getMrciCycles: ()                         => api.get<any[]>('/admin/mrci-cycles'),
  createMrciCycle: (d: any)                 => api.post('/admin/mrci-cycles', d),
  updateMrciCycle: (id: string, d: any)     => api.put(`/admin/mrci-cycles/${id}`, d),
  deleteMrciCycle: (id: string)               => api.delete(`/admin/mrci-cycles/${id}`),
  activateMrciCycle: (id: string)             => api.post(`/admin/mrci-cycles/${id}/activate`),
  processMrciCycle: (id: string)              => api.post<any>(`/admin/mrci-cycles/${id}/process`),
  getChatReports: ()                         => api.get<any[]>('/admin/chat-reports'),
  resolveChatReport: (id: string)             => api.patch(`/admin/chat-reports/${id}/resolve`),
  dismissChatReport: (id: string)             => api.patch(`/admin/chat-reports/${id}/dismiss`),
  // Game Servers
  getServers: ()                              => api.get<any[]>('/admin/servers'),
  createServer: (d: object)                   => api.post('/admin/servers', d),
  updateServer: (id: string, d: object)       => api.patch(`/admin/servers/${id}`, d),
  deleteServer: (id: string)                  => api.delete(`/admin/servers/${id}`),
  // Marketplace admin (A7–A10)
  getMarketplaceItems: (status?: string, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (status) params.append('status', status);
    return api.get<{ items: any[]; total: number }>(`/admin/marketplace/items?${params.toString()}`);
  },
  moderateMarketplaceItem: (itemId: string, action: 'approve' | 'reject', reason?: string) =>
    api.patch<{ id: string; status: string; message: string }>(`/admin/marketplace/items/${itemId}`, { action, reason }),
  getMarketplacePayouts: (status?: string, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (status) params.append('status', status);
    return api.get<{ payouts: any[]; total: number }>(`/admin/marketplace/payouts?${params.toString()}`);
  },
  approveMarketplacePayout: (payoutId: string) =>
    api.post<{ id: string; status: string; message: string }>(`/admin/marketplace/payouts/${payoutId}/approve`),
  rejectMarketplacePayout: (payoutId: string, reason?: string) =>
    api.post<{ id: string; status: string; message: string }>(`/admin/marketplace/payouts/${payoutId}/reject`, { reason }),
  releaseMarketplaceEscrow: () =>
    api.post<{ message: string; released_orders: number; released_total_brl: number }>('/admin/marketplace/escrow/release'),
  // Solicitações de associação (afiliado/parceiro)
  getMarketplaceRoleRequests: (status?: string, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (status) params.append('status', status);
    return api.get<{ requests: any[]; total: number }>(`/admin/marketplace/role-requests?${params.toString()}`);
  },
  approveMarketplaceRoleRequest: (requestId: string) =>
    api.post<{ id: string; status: string; message: string }>(`/admin/marketplace/role-requests/${requestId}/approve`),
  rejectMarketplaceRoleRequest: (requestId: string, reason?: string) =>
    api.post<{ id: string; status: string; message: string }>(`/admin/marketplace/role-requests/${requestId}/reject`, { reason }),
};

// ── Users ────────────────────────────────────────────────────

export const usersApi = {
  me:            ()                              => api.get('/users/me'),
  updateMe:      (d: { 
    display_name?: string; 
    avatar_url?: string; 
    bio?: string;
    location?: string;
    country_code?: string;
    preferred_language?: string;
    priority_game_id?: string;
    is_active?: boolean;
  }) => api.patch('/users/me', d),
  dismissXboxSuggestion: () => api.delete('/users/me/xbox-suggestion'),
  history: async (limit = 20, offset = 0, identifier = 'me', gameId?: string, categorySlug?: string) => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      identifier,
    });
    if (gameId) params.append('game_id', gameId);
    if (categorySlug) params.append('category_slug', categorySlug);

    try {
      const res = await fetch(`/api/pilot/history?${params.toString()}`, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        const items = (json.data || []) as UserHistoryItem[];
        (items as any).total = json.total ?? items.length;
        (items as any).total_pages = json.total_pages ?? 1;
        return items;
      }
    } catch { /* fallback */ }

    return api.get<UserHistoryItem[]>(`/users/${identifier}/history?${params.toString()}`);
  },
  ratingHistory: (gameId?: string, categorySlug?: string, days = 30, identifier = 'me') => {
    const params = new URLSearchParams({ days: days.toString() });
    if (gameId) params.append('game_id', gameId);
    if (categorySlug) params.append('category_slug', categorySlug);
    return api.get<any[]>(`/users/${identifier}/rating-history?${params.toString()}`);
  },
  stats:         (gameId?: string, categorySlug?: string, identifier = 'me') => {
    const params = new URLSearchParams();
    if (gameId) params.append('game_id', gameId);
    if (categorySlug) params.append('category_slug', categorySlug);
    return api.get<UserStats>(`/users/${identifier}/stats${params.toString() ? '?' + params.toString() : ''}`);
  },
  publicProfile: (username: string)              => api.get(`/users/${username}/public`),
  search:        (q: string, role?: string)      => api.get<any[]>(`/users/search?q=${q}${role ? `&role=${role}` : ''}`),
  listStewardsGlobal: ()                         => api.get<any[]>(`/users/stewards`),
  hardDelete:    ()                              => api.post('/users/me/hard-delete'),
};

// ── Teams ────────────────────────────────────────────────────

export interface TeamCreatePayload {
  name: string; tag: string;
  description?: string; logo_url?: string; discord_role_id?: string;
}

export interface TeamMember {
  id: string;
  user_id: string;
  member_role: 'leader' | 'manager' | 'pilot';
  is_promoter?: boolean;
  joined_at: string;
  username: string;
  display_name: string;
  avatar_url?: string;
  country_code?: string;
}

export interface TeamDetailResponse {
  team: {
    id: string;
    name: string;
    tag: string;
    description: string;
    logo_url: string;
    banner_url?: string;
    owner_id: string;
    created_at: string;
    stats?: {
      wins: number;
      podiums: number;
      events: number;
    };
  };
  members: TeamMember[];
}

export const teamsApi = {
  list:        (limit = 20, offset = 0, gameId?: string)     => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (gameId) params.append('game_id', gameId);
    return api.get(`/teams/?${params.toString()}`);
  },
  get:         (id: string)                 => api.get<TeamDetailResponse>(`/teams/${id}`),
  create:      (d: TeamCreatePayload)       => api.post('/teams/', d),
  update:      (id: string, data: Partial<TeamCreatePayload>) => api.patch(`/teams/${id}`, data),
  invite:      (teamId: string, data: any)  => api.post(`/teams/${teamId}/invitations`, data),
  acceptInvite:(invId: string)              => api.post(`/teams/invitations/${invId}/accept`),
  rejectInvite:(invId: string)              => api.post(`/teams/invitations/${invId}/reject`),
  cancelInvite:(invId: string)              => api.post(`/teams/invitations/${invId}/cancel`),
  listInvitations: ()                       => api.get<any[]>('/teams/invitations/me'),
  listPendingInvites: (teamId: string)      => api.get<any[]>(`/teams/${teamId}/invitations/pending`),
  leave: (teamId: string)                  => api.post(`/teams/${teamId}/leave`),
  removeMember: (teamId: string, memberId: string) => api.delete(`/teams/${teamId}/members/${memberId}`),
  togglePromoter: (teamId: string, memberId: string) => api.post(`/teams/${teamId}/members/${memberId}/toggle-promoter`),
};

// ── Team Internal Events ─────────────────────────────────────────────────────

export interface InternalEventScoring {
  [position: string]: number;
}

export interface InternalRaceResult {
  id: string;
  race_id: string;
  pilot_id: string;
  position: number | null;
  points: number;
  best_lap?: string;
  total_time?: string;
  dnf: boolean;
  notes?: string;
  pilot?: {
    id: string;
    display_name: string;
    username: string;
    avatar_url?: string;
    country_code?: string;
  };
}

export interface InternalRace {
  id: string;
  event_id: string;
  race_number: number;
  label?: string;
  circuit_name?: string;
  car_name?: string;
  scheduled_at?: string;
  laps?: number;
  notes?: string;
  is_finished: boolean;
  created_at: string;
  results?: InternalRaceResult[];
}

export interface InternalEvent {
  id: string;
  team_id: string;
  name: string;
  description?: string;
  image_url?: string;
  status: 'open' | 'closed';
  scoring_table: InternalEventScoring;
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  race_count?: number;
  finished_count?: number;
}

export interface InternalStanding {
  pilot_id: string;
  display_name: string;
  username: string;
  avatar_url?: string;
  country_code?: string;
  total_points: number;
  races: number;
  wins: number;
  podiums: number;
  dnfs?: number;
  rank: number;
}

export interface InternalEventDetail {
  event: InternalEvent;
  races: InternalRace[];
  standings: InternalStanding[];
}

export const teamInternalEventsApi = {
  list:          (teamId: string) =>
    api.get<InternalEvent[]>(`/teams/${teamId}/internal-events`),

  create:        (teamId: string, d: {
    name: string;
    description?: string;
    image_url?: string;
    notes?: string;
    scoring_table?: InternalEventScoring;
  }) => api.post<InternalEvent>(`/teams/${teamId}/internal-events`, d),

  get:           (teamId: string, eventId: string) =>
    api.get<InternalEventDetail>(`/teams/${teamId}/internal-events/${eventId}`),

  update:        (teamId: string, eventId: string, d: Partial<{
    name: string;
    description: string;
    image_url: string;
    notes: string;
    scoring_table: InternalEventScoring;
  }>) => api.patch<InternalEvent>(`/teams/${teamId}/internal-events/${eventId}`, d),

  toggleStatus:  (teamId: string, eventId: string) =>
    api.post<{ status: string }>(`/teams/${teamId}/internal-events/${eventId}/toggle-status`),

  addRace:       (teamId: string, eventId: string, d: {
    label?: string;
    circuit_name?: string;
    car_name?: string;
    scheduled_at?: string;
    laps?: number;
    notes?: string;
  }) => api.post<InternalRace>(`/teams/${teamId}/internal-events/${eventId}/races`, d),

  updateRace:    (teamId: string, eventId: string, raceId: string, d: Partial<{
    label: string;
    circuit_name: string;
    car_name: string;
    scheduled_at: string;
    laps: number;
    notes: string;
    is_finished: boolean;
  }>) => api.patch<InternalRace>(`/teams/${teamId}/internal-events/${eventId}/races/${raceId}`, d),

  launchResults: (teamId: string, eventId: string, raceId: string, results: {
    pilot_id: string;
    position?: number | null;
    best_lap?: string;
    total_time?: string;
    dnf?: boolean;
    notes?: string;
  }[]) => api.post(`/teams/${teamId}/internal-events/${eventId}/races/${raceId}/results`, { results }),

  getStandings:  (teamId: string) =>
    api.get<InternalStanding[]>(`/teams/${teamId}/internal-standings`),
};

// ── Leagues ──────────────────────────────────────────────────

export const leaguesApi = {
  list:         (publicOnly = true) => api.get(`/leagues/?public_only=${publicOnly}`),
  get:          (id: string)        => api.get(`/leagues/${id}`),
  delete:       (id: string)        => api.delete(`/leagues/${id}`),
  getBySlug:    (slug: string)      => api.get<{ league: any, seasons: any[], events: any[] }>(`/leagues/slug/${slug}`),
  create:       (d: object)         => api.post('/leagues/', d),
  update:       (id: string, d: object) => api.patch(`/leagues/${id}`, d),
  createSeason: (id: string, d: object) => api.post(`/leagues/${id}/seasons`, d),
  addSteward:    (id: string, pilotId: string) =>
    api.post(`/leagues/${id}/stewards?pilot_id=${pilotId}`),
  removeSteward: (id: string, userId: string) =>
    api.delete(`/leagues/${id}/stewards/${userId}`),
  listStewards:  (id: string) => api.get<any[]>(`/leagues/${id}/stewards`),
  checkEligibility: () => api.get<{ can_create: boolean, reason?: string }>('/leagues/check-eligibility'),
  checkSlugAvailability: (slug: string) => api.get<{ available: boolean }>(`/leagues/check-slug/${slug}`),
  listFollowed: ()                  => api.get<any[]>('/leagues/me/followed'),
  follow:       (id: string)        => api.post(`/leagues/${id}/follow`),
  unfollow:     (id: string)        => api.delete(`/leagues/${id}/follow`),
  listCoOrganizers: (id: string)    => api.get<any[]>(`/leagues/${id}/co-organizers`),
  addCoOrganizer: (id: string, userId: string) => 
    api.post(`/leagues/${id}/co-organizers?target_user_id=${userId}`),
  removeCoOrganizer: (id: string, userId: string) =>
    api.delete(`/leagues/${id}/co-organizers/${userId}`),
};

// ── Events ───────────────────────────────────────────────────

export const eventsApi = {
  createForLeague: (leagueId: string, d: object) => api.post(`/events/leagues/${leagueId}`, d),
  publish:         (id: string)                  => api.post(`/events/${id}/publish`),
  structure:       (id: string)                  => api.get<any[]>(`/events/${id}/structure`),
  addDay:          (id: string, d: object)       => api.post(`/events/${id}/days`, d),
  addRace:         (evId: string, dayId: string, d: object) =>
    api.post(`/events/${evId}/days/${dayId}/races`, d),
  addSession:      (evId: string, dayId: string, raceId: string, d: object) =>
    api.post(`/events/${evId}/days/${dayId}/races/${raceId}/sessions`, d),
  listByLeague:    (leagueId: string) => api.get<any[]>(`/events/leagues/${leagueId}/list`),
  getUpcomingFeed: (gameId?: string) => api.get<EventFeedResponse>(`/events/me/upcoming${gameId ? `?game_id=${gameId}` : ''}`),
  listAll:         (filters: { game_id?: string, category_id?: string, license_id?: string, event_type?: string, status?: string } = {}) => {
    const params = new URLSearchParams();
    if (filters.game_id) params.append('game_id', filters.game_id);
    if (filters.category_id) params.append('category_id', filters.category_id);
    if (filters.license_id) params.append('license_id', filters.license_id);
    if (filters.event_type) params.append('event_type', filters.event_type);
    if (filters.status) params.append('status', filters.status);
    return api.get<any[]>(`/events/?${params.toString()}`);
  },
  register:        (id: string, d: { car_id?: string; event_category_id?: string; car_name?: string; category_name?: string } = {}) => api.post(`/events/${id}/register`, d),
  unregister:      (id: string)       => api.delete(`/events/${id}/register`),
  registrations:   (id: string, includeReserves = false) => api.get<any[]>(`/events/${id}/registrations${includeReserves ? '?include_reserves=true' : ''}`),
  // Força do Grid (aba Analytics — liga Nível 3 / admin)
  gridStrength:    (id: string)       => api.get<any>(`/events/${id}/grid-strength`),
  // Benchmark da etapa (aba Analytics/Pit Lane — Nível 3, degradação justa — Entrega 2)
  trackBenchmark:  (id: string)       => api.get<TrackBenchmark>(`/events/${id}/track-benchmark`),
  // Calendário pessoal em .ics (Piloto PRO — gate no backend; 403 vira CTA)
  downloadMyCalendar: async (): Promise<Blob> => {
    const headers: Record<string, string> = {};
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/events/me/calendar.ics`, { headers, credentials: 'include' });
    if (!res.ok) {
      let detail: any = `HTTP ${res.status}`;
      try { detail = await res.json(); } catch { /* corpo não-JSON */ }
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },
  activateReserve: (eventId: string, registrationId: string, d: { replace_pilot_id?: string; replace_registration_id?: string } = {}) =>
    api.post(`/events/${eventId}/registrations/${registrationId}/activate-reserve`, d),
  getById:         (id: string)       => api.get<any>(`/events/${id}`),
  getStructure:   (id: string)       => api.get<any[]>(`/events/${id}/structure`),
  update:          (id: string, d: object) => api.patch(`/events/${id}`, d),
  syncStructure:   (id: string, d: object) => api.put(`/events/${id}/structure`, d),
  updateScoringSettings: (id: string, scoringSettings: any) => 
    api.patch(`/events/${id}`, { commission_settings: scoringSettings }),
  delete:          (id: string)       => api.delete(`/events/${id}`),
  generateSplits:  (id: string)       => api.post(`/events/${id}/generate-splits`),

  // Presets de configuração (pre-fill do formulário de criação) — por liga
  presets: {
    list: (leagueId: string, formKey?: string) =>
      api.get<any[]>(`/events/leagues/${leagueId}/presets${formKey ? `?form_key=${encodeURIComponent(formKey)}` : ''}`),
    create: (leagueId: string, d: { name: string; form_key: string; payload: object }) =>
      api.post<any>(`/events/leagues/${leagueId}/presets`, d),
    update: (presetId: string, d: { name?: string; payload?: object }) =>
      api.patch<any>(`/events/presets/${presetId}`, d),
    remove: (presetId: string) => api.delete(`/events/presets/${presetId}`),
  },
  
  // Stewards
  listStewards: (eventId: string) => api.get<any[]>(`/events/${eventId}/stewards`),
  addSteward:    (eventId: string, userId: string) => api.post(`/events/${eventId}/stewards`, { user_id: userId }),
  removeSteward: (eventId: string, userId: string) => api.delete(`/events/${eventId}/stewards/${userId}`),
  
  /** Upload de banner para o bucket 'events' (bypass RLS via backend) */
  upload: (file: File, folder: 'banners' | 'races' = 'banners') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ url: string }>(`/events/upload?folder=${folder}`, formData);
  },
  
  // Standings
  getStandings:    (id: string) => api.get<any[]>(`/events/${id}/standings`),
 
  // Fictitious Teams
  listTeams: (eventId: string) => api.get<any[]>(`/events/${eventId}/teams`),
  createTeam: (eventId: string, d: { name: string; tag?: string; car_id?: string | null }) => api.post(`/events/${eventId}/teams`, d),
  updateTeam: (eventId: string, teamId: string, d: { name?: string; tag?: string; car_id?: string | null }) => api.patch(`/events/${eventId}/teams/${teamId}`, d),
  deleteTeam: (eventId: string, teamId: string) => api.delete(`/events/${eventId}/teams/${teamId}`),
  updateRegistration: (eventId: string, registrationId: string, d: { event_team_id?: string | null; car_id?: string | null; event_category_id?: string | null }) => 
    api.patch(`/events/${eventId}/registrations/${registrationId}`, d),
  removeRegistration: (eventId: string, pilotId: string) =>
    api.delete(`/events/${eventId}/registrations/${pilotId}`),

  // Cars Management
  listCars: (eventId: string) => api.get<any[]>(`/events/${eventId}/cars`),
  addCar: (eventId: string, carId: string) => api.post(`/events/${eventId}/cars`, { car_id: carId }),
  removeCar: (eventId: string, carId: string) => api.delete(`/events/${eventId}/cars/${carId}`),

  // Categories Management
  listCategories: (eventId: string) => api.get<any[]>(`/events/${eventId}/categories`),
  createCategory: (eventId: string, d: { name: string; tag?: string; color_hex?: string; max_slots?: number }) => api.post(`/events/${eventId}/categories`, d),
  updateCategory: (eventId: string, categoryId: string, d: { name?: string; tag?: string; color_hex?: string; max_slots?: number }) => api.patch(`/events/${eventId}/categories/${categoryId}`, d),
  deleteCategory: (eventId: string, categoryId: string) => api.delete(`/events/${eventId}/categories/${categoryId}`),

  // Announcement Management
  sendAnnouncement: (id: string, message: string) => 
    api.post<{ status: string; message: string; count: number }>(`/events/${id}/announcements`, { message }),

  // Servers
  getServers: (eventId: string) => api.get<any[]>(`/events/${eventId}/servers`),

  // Catalog (cars & circuits — accessible to any authenticated user)
  catalog: {
    getCars:      (gameId?: string, categoryId?: string) => {
      const p = new URLSearchParams();
      if (gameId)      p.append('game_id', gameId);
      if (categoryId)  p.append('category_id', categoryId);
      return api.get<any[]>(`/events/catalog/cars${p.toString() ? '?' + p : ''}`);
    },
    getCircuits:  (gameId?: string) => {
      const p = new URLSearchParams();
      if (gameId) p.append('game_id', gameId);
      return api.get<any[]>(`/events/catalog/circuits${p.toString() ? '?' + p : ''}`);
    },
    getEventTypes: () => api.get<any[]>('/events/catalog/event-types'),
    getClassificationCategories: () => api.get<any[]>('/events/catalog/classification-categories'),
  },

  // AMS2 Event Config & Catalog Endpoints
  getAms2Config: (eventId: string) => api.get<any>(`/events/${eventId}/ams2-config`),
  saveAms2Config: (eventId: string, d: object) => api.post<any>(`/events/${eventId}/ams2-config`, d),
  getAms2Whitelist: (eventId: string) => api.get<{ whitelist_content: string; pilots_count: number }>(`/events/${eventId}/ams2-export/whitelist`),

  // AMS2 Race Configs (1:N — múltiplas corridas por evento)
  getAms2Races: (eventId: string) => api.get<any[]>(`/events/${eventId}/ams2-races`),
  saveAms2Race: (eventId: string, race: any) =>
    race.id
      ? api.put<any>(`/events/${eventId}/ams2-races/${race.id}`, race)
      : api.post<any>(`/events/${eventId}/ams2-races`, race),
  deleteAms2Race: (eventId: string, raceId: string) =>
    api.delete(`/events/${eventId}/ams2-races/${raceId}`),

  // Forza Event & Race Configs
  getForzaConfig: (eventId: string) => api.get<any>(`/events/${eventId}/forza-config`).catch(() => null),
  saveForzaConfig: (eventId: string, d: object) => api.post<any>(`/events/${eventId}/forza-config`, d),
  getForzaRaces: (eventId: string) => api.get<any[]>(`/events/${eventId}/forza-races`).catch(() => []),
  saveForzaRace: (eventId: string, race: any) =>
    race.id
      ? api.put<any>(`/events/${eventId}/forza-races/${race.id}`, race)
      : api.post<any>(`/events/${eventId}/forza-races`, race),
  deleteForzaRace: (eventId: string, raceId: string) =>
    api.delete(`/events/${eventId}/forza-races/${raceId}`),


  catalogAms2: {
    getTracks: () => api.get<any[]>('/events/catalog/ams2/tracks'),
    getClasses: () => api.get<any[]>('/events/catalog/ams2/classes'),
    getVehicles: (classServerId?: number) => {
      const p = new URLSearchParams();
      if (classServerId !== undefined) p.append('class_server_id', classServerId.toString());
      return api.get<any[]>(`/events/catalog/ams2/vehicles${p.toString() ? '?' + p : ''}`);
    }
  },
};

// ── Interfaces ────────────────────────────────────────────────

export interface EventFeedResponse {
  highlight: any | null;
  followed_leagues: any[];
  community: any[];
  all_registered: any[];
}

export interface ResultItem {
  pilot_id: string; 
  position: number;
  best_lap?: string; 
  laps_completed?: number; 
  total_time?: string;
  total_distance?: number;
  status?: string;
  is_pole?: boolean;
  is_fastest_lap?: boolean;
  extra_points_awarded?: Array<{ rule_id: string; label: string; points: number }>;
  sof?: number;
  event_category_id?: string;
  current_ers?: number;
  penal?: number;
  /** Tempo de penalidade (ex.: "0,500") já somado em `total_time` */
  penalty_time?: string | null;
  distance_driven_km?: number | null;
  is_dnf_calculated?: boolean;
}

export const resultsApi = {
  /** Preview do impacto de rating — sem salvar */
  preview: (sessionId: string, results: ResultItem[], splitId?: string) =>
    api.post<ResultItem[]>(`/results/${sessionId}/preview`, { results, split_id: splitId }),
  /** Lançamento definitivo via RPC atômico — IRREVERSÍVEL */
  publish: (sessionId: string, results: ResultItem[], splitId?: string) =>
    api.post(`/results/${sessionId}`, { results, split_id: splitId }),
  get: (sessionId: string) =>
    api.get<ResultItem[]>(`/results/${sessionId}`),
  /** Rascunho de lançamento parcial (RIVAIS) — salva sem calcular eRS/mRCI */
  saveDraft: (sessionId: string, results: ResultItem[]) =>
    api.put<{ saved: boolean; count: number }>(`/results/${sessionId}/draft`, { results }),
  /** Lê o rascunho parcial da sessão (results vazio se não houver) */
  getDraft: (sessionId: string) =>
    api.get<{ results: ResultItem[]; updated_at?: string | null }>(`/results/${sessionId}/draft`),
};

export const ocrApi = {
  scanForza: (files: File[]) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post<{ success: boolean; count: number; drivers: any[] }>('/ocr/forza', formData);
  }
};

// ── Sessions ─────────────────────────────────────────────────

export const sessionsApi = {
  getSplits:    (sessionId: string)           => api.get<any[]>(`/sessions/${sessionId}/splits`),
  entries:      (sessionId: string, confirmedOnly = false) =>
    api.get<any[]>(
      `/sessions/${sessionId}/entries${confirmedOnly ? '?confirmed_only=true' : ''}`,
    ),
  register:     (sessionId: string, splitId?: string) =>
    api.post(`/sessions/${sessionId}/entries`, { split_id: splitId }),
  cancelEntry:  (sessionId: string, pilotId: string) =>
    api.delete(`/sessions/${sessionId}/entries/${pilotId}`),
  searchPilots: (query: string)               => api.get<any[]>(`/users/search?q=${encodeURIComponent(query)}`),
};

// ── Incidents ────────────────────────────────────────────────

export interface Incident {
  incident_id: string;
  incident_type: string;
  session_id: string;
  session_type?: string;
  event_id?: string;
  lap_number: number;
  reported_pilot_name: string;
  reported_by_name: string;
  description: string;
  evidence_url: string;
  status: 'open' | 'review' | 'closed_penalized' | 'closed_dismissed' | 'closed_no_quorum';
  created_at: string;
  deadline_at: string;
  league_name: string;
  event_name: string;
  report_url?: string;
  defense_text?: string;
  defense_link?: string;
  defense_submitted_at?: string;
}

export type VoteType = 'none' | 'penalize' | 'dismiss' | 'abstain';
export type SeverityType = 'light' | 'medium' | 'grave' | 'gravissimo';

export interface StewardVote {
  steward_id: string;
  vote: VoteType;
  justification?: string;
  is_recused?: boolean;
  suggested_severity?: SeverityType;
  suggested_points?: number;
  users?: {
    display_name: string;
    avatar_url: string;
  };
}

export interface StewardQuorum {
  user_id: string;
  users?: {
    display_name: string;
    avatar_url: string;
  };
}

export interface IncidentDetailResponse {
  incident: Incident;
  votes: StewardVote[];
  quorum: StewardQuorum[];
  scoring_settings?: any;
  commission_settings?: {
    enable_incident_flow?: boolean;
    punishments?: {
      light: [number, number];
      medium: [number, number];
      grave: [number, number];
      gravissimo: [number, number];
    };
    dnf_points?: {
      enabled: boolean;
      fixed_value: number;
    };
    deadlines?: {
      defense_hours: number;
      report_hours?: number;
    };
  };
}

export interface IncidentPayload {
  session_id: string; incident_type: string; description: string;
  reported_pilot_id?: string; lap_number?: number; evidence_url?: string;
}

export const incidentsApi = {
  list: (params: { league_id?: string, status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.league_id) q.append('league_id', params.league_id);
    if (params.status) q.append('status', params.status);
    return api.get<Incident[]>(`/incidents/?${q.toString()}`);
  },
  myAssignments: () => api.get<Incident[]>('/incidents/my-assignments'),
  get: (id: string) => api.get<IncidentDetailResponse>(`/incidents/${id}`),
  create: (d: IncidentPayload) => api.post('/incidents/', d),
  vote: (id: string, d: { 
    vote: string; 
    justification?: string; 
    is_recused?: boolean;
    suggested_severity?: string;
    suggested_points?: number;
  }) => api.post(`/incidents/${id}/votes`, d),
  consolidate: (id: string) => api.post(`/incidents/${id}/consolidate`),
  generateReport: (id: string) => api.post<{ report_url: string }>(`/incidents/${id}/generate-report`),
  appeal: (id: string, d: object) => api.post(`/incidents/${id}/appeal`, d),
  submitDefense: (id: string, d: { defense_text: string, defense_link: string }) =>
    api.put(`/incidents/${id}/defense`, d),
};

// ── Tokens ───────────────────────────────────────────────────

export const tokensApi = {
  balance: ()                       => api.get('/tokens/balance'),
  history: (limit = 30, offset = 0) => api.get(`/tokens/history?limit=${limit}&offset=${offset}`),
  deposit: (d: { amount_brl_cents: number; gateway?: string; payment_method?: string }) =>
    api.post('/tokens/deposit', d),
  creditAll: (amount: number) =>
    api.post<{ status: string; credited: number; amount_each: number; total_credited: number }>(
      '/tokens/admin/credit-all', { amount },
    ),
};

// ── Race Check-ins ────────────────────────────────────────────

export const checkinsApi = {
  summary: (eventId: string) => api.get<any>(`/checkins/event/${eventId}`),
  race:    (raceId: string)  => api.get<any>(`/checkins/race/${raceId}`),
  set:     (raceId: string, d: { status: 'confirmed' | 'declined' | 'waitlist'; pilot_id?: string }) =>
    api.post<any>(`/checkins/${raceId}`, d),
  remove:  (raceId: string)  => api.delete<any>(`/checkins/${raceId}`),
  confirmAll: (raceId: string) =>
    api.post<{ status: string; race_id: string; created: number; skipped: number }>(
      `/checkins/${raceId}/confirm-all`, {},
    ),
  mine:    ()                => api.get<any[]>('/checkins/me'),
};

// ── Notifications ────────────────────────────────────────────

export const notificationsApi = {
  list:           () => api.get<any[]>('/notifications/'),
  markAsRead:     (id: string) => api.post(`/notifications/${id}/read`),
  markAllAsRead:  () => api.post('/notifications/read-all'),
};

// ── AMS2 Server Orchestration ────────────────────────────────

export interface ProvisionRequest {
  max_players_per_split?: number;  // default 12
  server_password?:        string;
  rcon_password?:          string;
}

export const ams2Api = {
  /** Provisiona servidores AMS2 para uma sessão (1 container por split). */
  provision: (sessionId: string, data: ProvisionRequest = {}) =>
    api.post(`/ams2/sessions/${sessionId}/provision`, data),

  /** Lista servidores de uma sessão. */
  listServers: (sessionId: string) =>
    api.get(`/ams2/sessions/${sessionId}/servers`),

  /** Status do container Docker + se resultados estão prontos. */
  serverStatus: (serverId: string) =>
    api.get(`/ams2/servers/${serverId}/status`),

  /** Força publicação manual dos resultados. */
  publishResults: (serverId: string) =>
    api.post(`/ams2/servers/${serverId}/publish`),

  /** Para o servidor sem publicar (emergência). */
  stopServer: (serverId: string) =>
    api.post(`/ams2/servers/${serverId}/stop`),
};

// ── AMS2 Servers ─────────────────────────────────────────────

export interface ProvisionPayload {
  max_players_per_split?: number;
  server_password?: string;
  rcon_password?: string;
}

export interface PublishPayload {
  /** { steam_id: pilot_uuid } */
  pilot_map: Record<string, string>;
}

export const serversApi = {
  /** Provisiona containers Docker para todos os splits da sessão */
  provision: (sessionId: string, data: ProvisionPayload = {}) =>
    api.post(`/servers/sessions/${sessionId}/provision`, data),

  /** Status de todos os servidores da sessão */
  sessionStatus: (sessionId: string) =>
    api.get(`/servers/sessions/${sessionId}/status`),

  /** Status de um split específico */
  splitStatus: (splitId: string) =>
    api.get(`/servers/splits/${splitId}/status`),

  /** Extrai resultados do AMS2 sem publicar (preview) */
  extractResults: (splitId: string, pilotMap: Record<string, string>) =>
    api.post(`/servers/splits/${splitId}/results/extract`, { pilot_map: pilotMap }),

  /** Extrai + publica resultados + para container — IRREVERSÍVEL */
  publishResults: (splitId: string, pilotMap: Record<string, string>) =>
    api.post(`/servers/splits/${splitId}/results/publish`, { pilot_map: pilotMap }),

  /** Para um container específico */
  stopSplit: (splitId: string) =>
    api.post(`/servers/splits/${splitId}/stop`),

  /** Para todos os containers da sessão */
  stopAll: (sessionId: string) =>
    api.post(`/servers/sessions/${sessionId}/stop-all`),
};

// ── Discord ────────────────────────────────────────────────
export const discordApi = {
  announceResultsImage: (payload: { channel_id?: string, image_base64: string, message?: string }) => 
    api.post('/discord/announce-results-image', payload),
  announceScheduleImage: (payload: { channel_id?: string, image_base64: string, message?: string }) =>
    api.post('/discord/announce-results-image', payload),
  announceLiveStream: (payload: { channel_id?: string, broadcaster_name: string, event_name: string, stream_url: string, platform: 'youtube' | 'twitch', message?: string }) =>
    api.post('/discord/announce-live-stream', payload),
  /** Envia composição dos splits como texto puro no canal padrão */
  announceSplitsText: (payload: { channel_id?: string, message: string }) =>
    api.post('/discord/announce-splits-text', payload),
};


// ── Broadcasting ────────────────────────────────────────────

export interface BroadcasterAvailability {
  broadcaster_id: string;
  display_name: string;
  username: string;
  avatar_url?: string | null;
  youtube_channel?: string | null;
  twitch_channel?: string | null;
  available_days: number[];   // 0=Dom, 1=Seg, ..., 6=Sáb
  available_start: string;    // 'HH:MM'
  available_end: string;      // 'HH:MM'
  confirmed_events: string[]; // event_ids confirmados
}

export interface BroadcasterCommitment {
  event_id: string;
  event_name: string;
  league_name?: string;
  scheduled_at: string;       // ISO date da primeira corrida
  confirmed_at: string;       // ISO date da confirmação
  registration_id: string;
}

export const broadcastingApi = {
  /** Retorna eventos publicados/em andamento sem narrador vinculado */
  getEventsWithoutBroadcaster: () =>
    api.get<any[]>('/broadcasting/events-without-broadcaster').catch(() => []),

  /** Retorna disponibilidade de todos os narradores (usado nos forms de evento) */
  getAllAvailability: () =>
    api.get<BroadcasterAvailability[]>('/broadcasting/availability').catch(() => []),

  /** Salva/atualiza disponibilidade do narrador logado */
  saveMyAvailability: (d: Partial<BroadcasterAvailability>) =>
    api.post('/broadcasting/availability', d),

  /** Retorna minha disponibilidade */
  getMyAvailability: () =>
    api.get<BroadcasterAvailability>('/broadcasting/availability/me').catch(() => null),

  /** Retorna os compromissos de transmissão do narrador logado */
  getMyCommitments: () =>
    api.get<BroadcasterCommitment[]>('/broadcasting/my-commitments').catch(() => []),

  /** Verifica se o narrador está ao vivo (YouTube/Twitch) */
  checkLiveStatus: (broadcasterId: string) =>
    api.get<{ is_live: boolean; platform?: string; stream_url?: string; title?: string }>(
      `/broadcasting/live-status/${broadcasterId}`
    ).catch(() => ({ is_live: false })),

  /** Retorna narradores atualmente ao vivo */
  getActiveLiveStreams: () =>
    api.get<{ broadcaster_name: string; stream_url: string; platform: string; event_name?: string }[]>(
      '/broadcasting/live-streams'
    ).catch(() => []),
};

// ── Analytics / Retention ────────────────────────────────────

export interface PageViewPayload {
  page: string;
  page_label?: string;
  duration_sec: number;
  session_token?: string;
}

export interface RetentionRow {
  page: string;
  page_label: string;
  avg_duration_sec: number;
  total_views: number;
  unique_users: number;
}

export interface RetentionDailyPoint {
  date: string;
  active_users: number;
  sessions: number;
}

export const analyticsApi = {
  /** Envia uma visualização de página com duração em segundos. Fire-and-forget. */
  trackPageView: (payload: PageViewPayload) =>
    api.post<void>('/analytics/page-view', payload, true /* silent */),

  /** Dados de retenção por tela (admin). */
  getRetention: (days = 30) =>
    api.get<RetentionRow[]>(`/analytics/admin/retention?days=${days}`),

  /** Série temporal diária de usuários ativos (admin). */
  getRetentionDaily: (days = 30) =>
    api.get<RetentionDailyPoint[]>(`/analytics/admin/retention/daily?days=${days}`),
};

// -- Chat Privado (Vercel Serverless TS /api/chat/* com Fallback) ─────
export interface BackendChatMessage {
  id: string;
  sender_id: string;
  recipient_id: string;
  message: string;
  created_at: string;
  delivered_at: string | null;
  read_at: string | null;
  chat_session_id: string | null;
}

export interface BackendConversationSummary {
  session_id: string;
  partner: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
  };
  lastMessage: string;
  lastAt: string;
  unreadCount: number;
}

async function chatFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  if (IS_DEV) {
    if (endpoint === '/conversations') {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [] as unknown as T;
        const { data: sessions } = await supabase
          .from('private_chat_sessions')
          .select(`
            id,
            participant1_id,
            participant2_id,
            p1:users!participant1_id(id, username, display_name, avatar_url),
            p2:users!participant2_id(id, username, display_name, avatar_url)
          `)
          .or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`);
        
        const convs = (sessions || []).map((s: any) => {
          const isP1 = s.participant1_id === user.id;
          const partner = isP1 ? s.p2 : s.p1;
          return {
            session_id: s.id,
            partner: {
              id: partner?.id || '',
              username: partner?.username || 'piloto',
              display_name: partner?.display_name || 'Piloto',
              avatar_url: partner?.avatar_url || null,
            },
            lastMessage: '',
            lastAt: new Date().toISOString(),
            unreadCount: 0,
          };
        });
        return convs as unknown as T;
      } catch {
        return [] as unknown as T;
      }
    }
  }

  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const vercelUrl = `/api/chat${endpoint}`;
  try {
    const res = await fetch(vercelUrl, { ...options, headers, credentials: 'include' });
    const contentType = res.headers.get('content-type') || '';

    if (res.ok) {
      if (res.status === 204) return undefined as unknown as T;
      if (!contentType.includes('application/json')) {
        return [] as unknown as T;
      }
      return await res.json();
    }
    return [] as unknown as T;
  } catch (err: any) {
    return [] as unknown as T;
  }
}


export const messagesApi = {
  getConversations: () =>
    chatFetch<BackendConversationSummary[]>('/conversations'),

  getMessages: (sessionId: string) =>
    chatFetch<{ session_id: string; messages: BackendChatMessage[] }>(`/session?id=${encodeURIComponent(sessionId)}`),

  getMessagesByPartner: (partnerId: string) =>
    chatFetch<{ session_id: string; messages: BackendChatMessage[] }>(`/partner?id=${encodeURIComponent(partnerId)}`),

  sendMessage: (recipientId: string, message: string, chatSessionId?: string) =>
    chatFetch<BackendChatMessage>('/send', {
      method: 'POST',
      body: JSON.stringify({ recipient_id: recipientId, message, chat_session_id: chatSessionId }),
    }),

  hideSession: (sessionId: string) =>
    chatFetch<{ success: boolean }>('/hide', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    }),

  markAsRead: (sessionId: string) =>
    chatFetch<{ success: boolean }>('/read', {
      method: 'PATCH',
      body: JSON.stringify({ session_id: sessionId }),
    }),

  reportMessage: (messageId: string, payload: { reason: string; details?: string }) =>
    chatFetch<any>('/report', {
      method: 'POST',
      body: JSON.stringify({ message_id: messageId, ...payload }),
    }),
};

// ── AMS2 Lap Telemetry ────────────────────────────────────────

export interface LeaderboardEntry {
  id:                    string;
  session_id:            string;
  stage_key:             string;
  track_id:              number;
  vehicle_class_id:      number;
  track_name?:           string;
  vehicle_class_name?:   string;
  category_name?:        string;
  car_model?:            string;
  participant_steam_id:  string;
  participant_name:      string;
  best_lap_number:       number;
  best_lap_ms:           number;
  best_sector1_ms?:      number;
  best_sector2_ms?:      number;
  best_sector3_ms?:      number;
  /** Se a melhor volta é válida (limpa) ou suja (com corte de pista) */
  is_best_lap_valid:     boolean;
  /** Nº de cortes na melhor volta */
  best_lap_cuts:         number;
  pilot_id?:             string;
  lap_count:             number;
  total_cuts:            number;
  total_distance_m?:     number;
  display_name?:         string;
  username?:             string;
  avatar_url?:           string;
  country_code?:         string;
  // Calculados pelo backend
  rank:                  number;
  delta_ms:              number;
}

export interface LeaderboardFilters {
  trackId?:        number;
  vehicleClassId?: number;
  stageKey?:       string;
  steamId?:        string;
  limit?:          number;
  offset?:         number;
}

export const lapsApi = {
  /** Importa voltas do sms_stats_data.json — idempotente via ON CONFLICT */
  importFromStats: (sessionId: string, statsJson: unknown) =>
    api.post<{ inserted: number; duplicates: number; total: number; summary: any }>(
      `/results/${sessionId}/laps`,
      { stats_json: statsJson },
    ),

  /** Lista voltas de uma sessão (expansão de piloto no leaderboard) */
  getSessionLaps: (sessionId: string, steamId?: string, stageKey?: string) => {
    const p = new URLSearchParams();
    if (steamId)  p.append('steam_id',  steamId);
    if (stageKey) p.append('stage_key', stageKey);
    return api.get<any[]>(`/results/${sessionId}/laps${p.toString() ? '?' + p : ''}`);
  },

  /** Leaderboard: melhor volta por piloto × pista × classe */
  leaderboard: (filters: LeaderboardFilters = {}) => {
    const p = new URLSearchParams();
    if (filters.trackId        != null) p.append('track_id',         String(filters.trackId));
    if (filters.vehicleClassId != null) p.append('vehicle_class_id', String(filters.vehicleClassId));
    if (filters.stageKey)               p.append('stage_key',        filters.stageKey);
    if (filters.steamId)                p.append('steam_id',         filters.steamId);
    if (filters.limit   != null)        p.append('limit',            String(filters.limit));
    if (filters.offset  != null)        p.append('offset',           String(filters.offset));
    return api.get<LeaderboardEntry[]>(`/results/leaderboard${p.toString() ? '?' + p : ''}`);
  },

  /**
   * Leaderboard direto das voltas importadas do servidor (session_id IS NULL).
   * Inclui todos os pilotos, registrados ou não. Não depende da view ams2_leaderboard.
   */
  serverLeaderboard: (filters: LeaderboardFilters = {}) => {
    const p = new URLSearchParams();
    if (filters.trackId        != null) p.append('track_id',         String(filters.trackId));
    if (filters.vehicleClassId != null) p.append('vehicle_class_id', String(filters.vehicleClassId));
    if (filters.stageKey)               p.append('stage_key',        filters.stageKey);
    if (filters.steamId)                p.append('steam_id',         filters.steamId);
    if (filters.limit   != null)        p.append('limit',            String(filters.limit));
    return api.get<LeaderboardEntry[]>(`/results/server-leaderboard${p.toString() ? '?' + p : ''}`);
  },
};

export const lapRecordsApi = {
  upsertBulk: (data: {
    items: {
      pilot_id: string;
      best_lap_ms: number;
      best_lap_str: string;
      car_model?: string;
      category_name?: string;
      laps_completed?: number;
    }[];
    event_id: string;
    session_id?: string;
    track_name?: string;
    circuit_layout_id?: string;
  }) => api.post('/lap-records/upsert-bulk', data),
  getByEvent: (eventId: string, categoryName?: string) =>
    api.get<any[]>(`/lap-records/by-event/${eventId}${categoryName ? `?category_name=${encodeURIComponent(categoryName)}` : ''}`),
  getByTrack: (trackName?: string, circuitLayoutId?: string, categoryName?: string) => {
    const p = new URLSearchParams();
    if (trackName) p.append('track_name', trackName);
    if (circuitLayoutId) p.append('circuit_layout_id', circuitLayoutId);
    if (categoryName) p.append('category_name', categoryName);
    return api.get<any[]>(`/lap-records/by-track${p.toString() ? '?' + p : ''}`);
  },
};


// ── Emperor Server Manager ────────────────────────────────────

export interface EmperorPlace {
  position:   number;
  steam_id:   string;
  name:       string;
  total_time?: string;
  best_lap?:   string;
  laps?:       number;
  class_pos?:  number;
}

export interface EmperorPreviewResponse {
  places:    EmperorPlace[];
  matched:   (EmperorPlace & { pilot_id: string; display_name: string })[];
  unmatched: string[];
  meta: {
    track?:     string;
    race_laps?: number;
    date?:      string;
  };
}

export const emperorApi = {
  /** Verifica se a API Emperor está acessível */
  healthcheck: () =>
    api.get<{ ok: boolean; data: any }>('/emperor/healthcheck'),

  /** Lista resultados disponíveis no Emperor */
  listResults: (page = 0, q = '') =>
    api.get<any>(`/emperor/results?page=${page}&q=${encodeURIComponent(q)}`),

  /** Preview de um resultado: pilotos + match com plataforma */
  previewResult: (filename: string, serverId = 0) =>
    api.get<EmperorPreviewResponse>(
      `/emperor/results/${encodeURIComponent(filename)}/preview?server_id=${serverId}`,
    ),

  /** Lança resultado do Emperor para uma session da plataforma */
  launch: (payload: {
    session_id:  string;
    filename:    string;
    server_id?:  number;
    split_id?:   string;
    overrides?:  { steam_id: string; pilot_id: string }[];
  }) => api.post<any>('/emperor/results/launch', payload),
};


// ── Marketplace ────────────────────────────────────────────────

export interface MarketplaceCategory {
  id: string;
  slug: string;
  name: string;
  description?: string;
  commission_rate: number;
  icon_name?: string;
  display_order: number;
}

export interface MarketplaceSeller {
  id: string;
  user_id: string;
  store_name: string;
  store_slug?: string;
  bio?: string;
  pix_key_type?: string;
  pix_key?: string;
  total_sales: number;
  rating_avg: number;
  rating_count: number;
  balance_brl: number;
  is_verified: boolean;
}

export interface MarketplacePurchase {
  id: string;
  item_id: string;
  seller_id: string;
  amount_brl: number;
  commission_amount: number;
  net_amount: number;
  payment_status: string;
  payment_gateway: string;
  paid_at: string | null;
  download_count: number;
  download_expires_at: string | null;
  marketplace_items?: {
    id: string;
    title: string;
    description: string;
    game_slug: string;
    car_name?: string;
    track_name?: string;
    preview_urls: string[];
    file_format?: string;
  };
  marketplace_sellers?: { store_name: string };
}

export interface MarketplaceItemEventLink {
  link_id?: string;
  event_id: string;
  event_name?: string;
  league_id?: string;
  league_name?: string;
  league_access_level?: number;
  require_active_registration: boolean;
}

export interface MarketplaceItem {
  id: string;
  seller_id: string;
  category_id: string;
  game_slug: string;
  title: string;
  description: string;
  price_brl: number;
  car_name?: string;
  track_name?: string;
  game_version?: string;
  file_url?: string;
  file_size_bytes?: number;
  file_format?: string;
  preview_urls: string[];
  status: string;
  sales_count: number;
  rating_avg: number;
  rating_count: number;
  created_at: string;
  marketplace_sellers?: Partial<MarketplaceSeller>;
  marketplace_categories?: Partial<MarketplaceCategory>;
  already_purchased?: boolean;
  purchased_order_id?: string | null;
  already_reviewed?: boolean;
  reviews?: any[];
  exclusive_events?: MarketplaceItemEventLink[];
  event_locked?: boolean;
  eligible?: boolean;
}

export const marketplaceApi = {
  getCategories: () => api.get<MarketplaceCategory[]>('/marketplace/categories'),
  getItems: (params: { game?: string; category?: string; search?: string; min_price?: number; max_price?: number; sort?: string; page?: number; limit?: number; event_id?: string } = {}) => {
    const p = new URLSearchParams();
    if (params.game) p.append('game', params.game);
    if (params.category) p.append('category', params.category);
    if (params.search) p.append('search', params.search);
    if (params.min_price !== undefined) p.append('min_price', String(params.min_price));
    if (params.max_price !== undefined) p.append('max_price', String(params.max_price));
    if (params.event_id) p.append('event_id', params.event_id);
    if (params.sort) p.append('sort', params.sort);
    if (params.page) p.append('page', String(params.page));
    if (params.limit) p.append('limit', String(params.limit));
    return api.get<{ items: MarketplaceItem[]; page: number; limit: number }>(`/marketplace/items?${p.toString()}`);
  },
  getItemDetail: (id: string) => api.get<MarketplaceItem>(`/marketplace/items/${id}`),
  // Eventos com itens vinculados (alimenta o filtro da vitrine)
  getEventsWithItems: () => api.get<{ id: string; name: string; starts_at?: string }[]>('/marketplace/events-with-items'),
  // Perfil da loja leve (home do Marketplace): loja + itens aguardando moderação
  getSellerProfile: () => api.get<{ seller: MarketplaceSeller | null; pending_items: number }>('/marketplace/seller/profile'),
  // Associação: afiliado / parceiro
  getAffiliateRequest: () => api.get<{ request: any | null; has_affiliado: boolean; has_parceiro: boolean }>('/marketplace/affiliate/request'),
  requestAffiliate: (data: { requested_role: 'afiliado' | 'parceiro'; message?: string }) =>
    api.post<{ id: string; status: string; requested_role: string; message_feedback: string }>('/marketplace/affiliate/request', data),
  onboardSeller: (data: { store_name: string; bio?: string; pix_key_type: string; pix_key: string }) => api.post<MarketplaceSeller>('/marketplace/seller/onboard', data),
  getSellerDashboard: () => api.get<{
    seller: MarketplaceSeller;
    items: MarketplaceItem[];
    recent_orders: any[];
    payouts: any[];
    escrow: {
      held_brl: number;
      available_brl: number;
      pending_orders: number;
      pending_brl: number;
      next_release_at: string | null;
    };
  }>('/marketplace/seller/dashboard'),
  createItem: (data: any) => api.post<MarketplaceItem>('/marketplace/items', data),
  updateItem: (id: string, data: any) => api.patch<MarketplaceItem>(`/marketplace/items/${id}`, data),
  archiveItem: (id: string) => api.delete<{ status: string }>(`/marketplace/items/${id}`),
    createCheckout: (itemId: string) =>
      apiFetch<{ order_id: string; amount_brl: number; pix_qrcode_payload: string | null; status: string; idempotent_replay?: boolean }>(
        `/marketplace/checkout/${itemId}`,
        { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() } }
      ),
    checkCheckoutEligibility: (itemId: string) =>
      api.post<{ eligible: boolean; reason: string | null; events: any[] }>(
        `/marketplace/checkout/${itemId}/events-check`, {}
      ),
    confirmCheckout: (orderId: string) =>
    apiFetch<{ status: string; download_token: string; already_paid?: boolean; message: string }>(
      `/marketplace/checkout/${orderId}/confirm`,
      { method: 'POST' }
    ),
    getPurchases: () => api.get<MarketplacePurchase[]>('/marketplace/purchases'),
    getDownloadUrl: (orderId: string) => api.get<{ download_url: string; title: string; expires_in_seconds: number }>(`/marketplace/purchases/${orderId}/download`),
    // A12: rotaciona/expõe download_token validando download_expires_at
    rotateDownloadToken: (orderId: string) =>
      api.post<{ order_id: string; download_token: string; download_expires_at: string | null; download_count: number }>(
        `/marketplace/purchases/${orderId}/download-token`, {}
      ),
  requestPayout: (amountBrl: number) =>
    apiFetch<{ payout: any; new_balance_brl: number | null; idempotent_replay?: boolean; message: string }>(
      '/marketplace/seller/payout-request',
      {
        method: 'POST',
        body: JSON.stringify({ amount_brl: amountBrl }),
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }
    ),
  createReview: (itemId: string, data: { order_id: string; rating: number; comment?: string }) => api.post<any>(`/marketplace/items/${itemId}/reviews`, data),
  // Exclusividade por evento
  listMyStaffEvents: () => api.get<any[]>('/marketplace/my/staff-events'),
  listItemEvents: (itemId: string) => api.get<MarketplaceItemEventLink[]>(`/marketplace/items/${itemId}/events`),
  linkItemEvent: (itemId: string, data: { event_id: string; require_active_registration?: boolean }) =>
    api.post<MarketplaceItemEventLink>(`/marketplace/items/${itemId}/events`, data),
  unlinkItemEvent: (itemId: string, eventId: string) =>
    api.delete<{ status: string; message: string }>(`/marketplace/items/${itemId}/events/${eventId}`),
};

// ── Ligas (nível de acesso) ─────────────────────────────────────
export const leaguesAccessApi = {
  updateAccessLevel: (leagueId: string, accessLevel: number) =>
    api.patch<{ id: string; name?: string; access_level: number; message: string }>(
      `/leagues/${leagueId}/access-level`,
      { access_level: accessLevel }
    ),
};

// ── Chat de Evento (Vercel Serverless TS /api/event-chat/*) ────────────
export interface EventChatParticipant {
  id: string;
  display_name: string;
  username?: string;
  avatar_url?: string | null;
  role?: 'pilot' | 'steward' | 'organizer';
}

export interface EventChatChannel {
  id: string;
  event_id: string;
  name: string;
  type: 'public' | 'announcement' | 'stewards';
  description: string | null;
  /**
   * true = canal de voz (áudio em tempo real).
   * Sem esta flag os canais de voz somem da sidebar, já que o cliente
   * separa texto/voz por `is_voice`.
   */
  is_voice?: boolean;
  created_at: string;
}

export interface EventChatMessage {
  id: string;
  event_id: string;
  channel_id: string;
  sender_id: string;
  message: string;
  reply_to_id?: string | null;
  is_pinned?: boolean;
  created_at: string;
  updated_at?: string | null;
  sender: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
  };
}

async function eventChatFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const vercelUrl = `/api/event-chat${endpoint}`;
  try {
    const res = await fetch(vercelUrl, { ...options, headers, credentials: 'include' });
    const contentType = res.headers.get('content-type') || '';

    if (res.ok) {
      if (res.status === 204) return undefined as unknown as T;
      if (!contentType.includes('application/json')) {
        throw new ApiError(500, 'Serviço de chat temporariamente indisponível.');
      }
      return await res.json();
    }

    let detail = `HTTP ${res.status}`;
    try {
      if (contentType.includes('application/json')) {
        const json = await res.json();
        detail = json.detail ?? json.message ?? json.error ?? detail;
      }
    } catch {}
    throw new ApiError(res.status, detail);
  } catch (err: any) {
    if (err instanceof ApiError) throw err;
    console.error(`[eventChatFetch] Vercel API fetch failed for ${vercelUrl}:`, err?.message || err);
    throw new ApiError(500, err?.message || 'Falha ao conectar com o serviço de chat do evento.');
  }
}

const IS_DEV = import.meta.env.MODE === 'development';

// Credenciais Supabase — anon key é pública por design; segurança via RLS.
const SB_URL = 'https://vxandpltgyowfdtbkcat.supabase.co';
const SB_ANON =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZ4YW5kcGx0Z3lvd2ZkdGJrY2F0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzUzMDEyMDcsImV4cCI6MjA5MDg3NzIwN30.7_rWC_98IE4rbSyD2BoIwqLnEtAEWxZgcRpyn__e__0';

/** Chama um RPC Supabase via fetch direto, evitando dependência do SDK para headers. */
async function sbRpc<T>(fn: string, params: Record<string, unknown>, userToken?: string | null): Promise<T> {
  const url = `${SB_URL}/rest/v1/rpc/${fn}?apikey=${SB_ANON}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SB_ANON,
      'Authorization': userToken ? `Bearer ${userToken}` : `Bearer ${SB_ANON}`,
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body?.message || body?.error || `RPC ${fn} falhou: HTTP ${res.status}`);
  }
  return res.json();
}

// Helper para resolver channel_id sintético em UUID real do banco
async function resolveChannelId(eventId: string, channelId: string): Promise<string> {
  if (channelId && !channelId.startsWith('default-')) return channelId;
  try {
    const { data } = await (supabase as any)
      .from('event_chat_channels')
      .select('id')
      .eq('event_id', eventId)
      .eq('name', 'geral')
      .maybeSingle();

    if (data && (data as any).id) return (data as any).id;

    await sbRpc('ensure_event_chat_channels', { p_event_id: eventId });
    const { data: created } = await (supabase as any)
      .from('event_chat_channels')
      .select('id')
      .eq('event_id', eventId)
      .eq('name', 'geral')
      .maybeSingle();

    if (created && (created as any).id) return (created as any).id;
  } catch {}
  return channelId;
}

export const eventChatApi = {
  getChannels: async (eventId: string, currentUserId?: string): Promise<EventChatChannel[]> => {
    if (!eventId) return [];
    if (!IS_DEV) {
      try {
        const res = await eventChatFetch<EventChatChannel[]>(`/channels?event_id=${eventId}`);
        if (res && res.length > 0) return res;
      } catch (err: any) {
        if (!(err?.statusCode === 404 || err?.status === 404 || String(err?.message || '').includes('404'))) {
          console.warn('[eventChatApi.getChannels] Vercel API fallback for channels:', err?.message || err);
        }
      }
    }
    let userId = currentUserId;
    let token: string | null = null;
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id;
      token = session?.access_token || null;
    }
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id || '00000000-0000-0000-0000-000000000000';
    }

    try {
      const data = await sbRpc<EventChatChannel[]>('get_event_chat_channels', {
        p_event_id: eventId,
        p_user_id: userId,
      }, token);
      if (data && data.length > 0) return data;
    } catch {}

    try {
      await sbRpc('ensure_event_chat_channels', { p_event_id: eventId });
      // `is_voice` é obrigatório aqui: sem ele os canais de voz deixam de
      // aparecer na sidebar quando este fallback é usado (dev ou API indisponível).
      const { data: defaultChannels } = await supabase
        .from('event_chat_channels')
        .select('id, event_id, name, type, description, is_voice, created_at')
        .eq('event_id', eventId)
        .order('created_at', { ascending: true });

      if (defaultChannels && defaultChannels.length > 0) return defaultChannels;
    } catch {}

    return [
      { id: `default-geral-${eventId}`, event_id: eventId, name: 'geral', type: 'public', description: 'Chat público de texto do evento para todos os pilotos', is_voice: false, created_at: new Date().toISOString() },
      { id: `default-avisos-${eventId}`, event_id: eventId, name: 'avisos', type: 'announcement', description: 'Canal oficial de anúncios e briefing da organização', is_voice: false, created_at: new Date().toISOString() },
    ];
  },

  getMessages: async (channelId: string, eventId: string, before?: string): Promise<{ channel_id: string; messages: EventChatMessage[]; has_more: boolean }> => {
    const targetChannelId = await resolveChannelId(eventId, channelId);

    if (!IS_DEV) {
      try {
        const res = await eventChatFetch<{ channel_id: string; messages: EventChatMessage[]; has_more: boolean }>(
          `/messages?channel_id=${targetChannelId}&event_id=${eventId}${before ? `&before=${encodeURIComponent(before)}` : ''}`
        );
        if (res && Array.isArray(res.messages)) return res;
      } catch (err: any) {
        if (!(err?.statusCode === 404 || err?.status === 404 || String(err?.message || '').includes('404'))) {
          console.warn('[eventChatApi.getMessages] Vercel API fallback for messages:', err?.message || err);
        }
      }
    }

    let query = supabase
      .from('event_chat_messages')
      .select(`
        id,
        event_id,
        channel_id,
        sender_id,
        message,
        reply_to_id,
        is_pinned,
        created_at,
        updated_at,
        sender:users!sender_id (
          id,
          username,
          display_name,
          avatar_url
        )
      `)
      .eq('channel_id', targetChannelId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (before) {
      query = query.lt('created_at', before);
    }

    const { data: rawMessages, error } = await query;
    if (error) throw new ApiError(500, error.message);

    const messages = (rawMessages || []).map((msg: any) => ({
      id: msg.id,
      event_id: msg.event_id,
      channel_id: msg.channel_id,
      sender_id: msg.sender_id,
      message: msg.message,
      reply_to_id: msg.reply_to_id,
      is_pinned: msg.is_pinned,
      created_at: msg.created_at,
      updated_at: msg.updated_at,
      sender: {
        id: msg.sender?.id || msg.sender_id,
        username: msg.sender?.username || 'piloto',
        display_name: msg.sender?.display_name || 'Piloto',
        avatar_url: msg.sender?.avatar_url || null,
      },
    })).reverse();

    return { channel_id: targetChannelId, messages, has_more: (rawMessages || []).length === 50 };
  },

  sendMessage: async (eventId: string, channelId: string, message: string, replyToId?: string, senderId?: string): Promise<EventChatMessage> => {
    const targetChannelId = await resolveChannelId(eventId, channelId);

    if (!IS_DEV) {
      try {
        return await eventChatFetch<EventChatMessage>('/send', {
          method: 'POST',
          body: JSON.stringify({
            event_id: eventId,
            channel_id: targetChannelId,
            message,
            reply_to_id: replyToId || null,
          }),
        });
      } catch (err: any) {
        if (!(err?.statusCode === 404 || err?.status === 404 || String(err?.message || '').includes('404'))) {
          console.warn('[eventChatApi.sendMessage] Vercel API fallback:', err?.message || err);
        }
      }
    }

    let userId = senderId;
    // O token é obrigatório mesmo quando `senderId` já veio de fora: a RPC
    // valida o remetente contra auth.uid() (via get_my_id()) e recusa chamadas
    // sem JWT de usuário.
    let userToken: string | null = null;

    const { data: { session } } = await supabase.auth.getSession();
    userToken = session?.access_token || null;
    if (!userId) {
      userId = session?.user?.id;
    }

    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id;
    }

    if (!userId) throw new ApiError(401, 'Usuário não autenticado.');
    if (!userToken) throw new ApiError(401, 'Sessão expirada. Entre novamente para enviar mensagens.');

    // 1. Tenta RPC send_event_chat_message se disponível
    try {
      const inserted = await sbRpc<EventChatMessage>('send_event_chat_message', {
        p_event_id: eventId,
        p_channel_id: targetChannelId,
        p_sender_id: userId,
        p_message: message,
        p_reply_to_id: replyToId || null,
      }, userToken);
      if (inserted && inserted.id) return inserted;
    } catch (rpcErr: any) {
      // A RPC é o ÚNICO caminho de escrita: valida permissão por tipo de canal
      // (#avisos/#stewards) e escapa do RLS via SECURITY DEFINER. Um fallback de
      // inserção direta seria barrado pelo RLS e mascararia o erro real.
      console.error('[eventChatApi.sendMessage] RPC send_event_chat_message falhou:', rpcErr);
      throw new ApiError(
        rpcErr?.statusCode || 500,
        rpcErr?.message || 'Não foi possível enviar a mensagem no chat do evento.'
      );
    }

    throw new ApiError(500, 'Não foi possível enviar a mensagem no chat do evento.');
  },

  pinMessage: async (messageId: string, isPinned: boolean): Promise<EventChatMessage> => {
    if (!IS_DEV) {
      try {
        return await eventChatFetch<EventChatMessage>('/pin', {
          method: 'POST',
          body: JSON.stringify({ message_id: messageId, is_pinned: isPinned }),
        });
      } catch (err: any) {
        if (!(err?.statusCode === 404 || err?.status === 404 || String(err?.message || '').includes('404'))) {
          throw err;
        }
      }
    }

    const { data, error } = await (supabase as any)
      .from('event_chat_messages')
      .update({ is_pinned: isPinned })
      .eq('id', messageId)
      .select('id, channel_id, event_id, sender_id, message, reply_to_id, is_pinned, created_at, updated_at')
      .single();

    if (error) throw new ApiError(500, error.message);
    return data as EventChatMessage;
  },
};

// ── Voz de Evento (Vercel Serverless TS /api/event-voice/*) ────────────
export interface EventVoiceParticipant {
  channel_id: string;
  user_id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  is_muted: boolean;
  is_deafened: boolean;
  joined_at: string;
  is_speaking?: boolean;
}

/** Ações de moderação de voz (server-side, via `POST /api/event-voice/moderate`). */
export type VoiceModerationAction = 'mute' | 'unmute' | 'mute_all' | 'unmute_all';

/**
 * Modo de áudio do evento (persistido em `events.voice_isolation_mode`):
 * - 'everyone': todos ouvem todos (padrão);
 * - 'teams': cada piloto só ouve/só é ouvido pela própria equipe; a comissão
 *   ouve e é ouvida por todos.
 */
export type VoiceIsolationMode = 'everyone' | 'teams';

export interface VoiceModerationResult {
  success: boolean;
  action: VoiceModerationAction;
  /** Alvos atingidos com sucesso. */
  applied: number;
  /** Alvos que falharam (sala instável, trilha ausente, etc). */
  failed: number;
  muted: boolean;
  targets: string[];
}

export interface EventVoiceToken {
  /** JWT assinado pelo servidor — curto (15m), nunca persistir. */
  token: string;
  /** wss://<projeto>.livekit.cloud */
  url: string;
  /** `voice-room-<channelId>` */
  room: string;
  /** `public.users.id` — o mesmo id usado em event_voice_participants. */
  identity: string;
  channel: { id: string; name: string; type: string };
}

export const eventVoiceApi = {
  getParticipants: async (eventId: string): Promise<EventVoiceParticipant[]> => {
    if (!eventId) return [];
    if (!IS_DEV) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`/api/event-voice/participants?event_id=${eventId}`, {
          headers: { Authorization: `Bearer ${session?.access_token || ''}` },
        });
        if (res.ok) return await res.json();
      } catch {}
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const data = await sbRpc<EventVoiceParticipant[]>('get_event_voice_participants', {
        p_event_id: eventId,
      }, session?.access_token);
      return data || [];
    } catch {
      return [];
    }
  },

  updatePresence: async (
    eventId: string,
    channelId: string,
    action: 'join' | 'leave' | 'update',
    isMuted = false,
    isDeafened = false,
    /**
     * `public.users.id` (o id do perfil), NÃO o uid do Supabase Auth.
     * event_voice_participants.user_id tem FK para users(id).
     */
    userId?: string
  ) => {
    if (!IS_DEV) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch('/api/event-voice/participants', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token || ''}`,
          },
          body: JSON.stringify({
            event_id: eventId,
            channel_id: channelId,
            action,
            is_muted: isMuted,
            is_deafened: isDeafened,
          }),
        });
        if (res.ok) return await res.json();
      } catch {}
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();

      let profileId = userId || null;
      if (!profileId) {
        // Resolve o perfil a partir do Supabase Auth (o id do Auth nunca é o
        // mesmo de users.id neste banco).
        const { data: authUser } = await supabase.auth.getUser();
        if (authUser.user) {
          const { data: profile } = await (supabase as any)
            .from('users')
            .select('id')
            .eq('supabase_uid', authUser.user.id)
            .maybeSingle();
          profileId = (profile as any)?.id || null;
        }
      }
      if (!profileId) return null;

      const result = await sbRpc<unknown>('set_event_voice_presence', {
        p_event_id: eventId,
        p_channel_id: channelId,
        p_user_id: profileId,
        p_action: action,
        p_is_muted: isMuted,
        p_is_deafened: isDeafened,
      }, session?.access_token);
      return result;
    } catch (err: any) {
      console.warn('[eventVoiceApi] Falha na presença de voz:', err?.message || err);
      return null;
    }
  },

  /**
   * POST autenticado para /api/event-voice/*.
   *
   * Sem guard `IS_DEV` e sem fallback para RPC de propósito: o token é assinado
   * com LIVEKIT_API_SECRET e a moderação passa pelo SFU — nenhum dos dois existe
   * no cliente. Em `vercel dev` a API roda na mesma origem, então o fetch funciona.
   */
  _post: async <T,>(endpoint: string, payload: Record<string, unknown>): Promise<T> => {
    const { data: { session } } = await supabase.auth.getSession();

    let res: Response;
    try {
      res = await fetch(`/api/event-voice${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify(payload),
      });
    } catch {
      throw new ApiError(
        0,
        'Sem conexão com o servidor de voz. Localmente é preciso rodar com `vercel dev`.'
      );
    }

    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        if ((res.headers.get('content-type') || '').includes('application/json')) {
          const json = await res.json();
          detail = json.detail ?? json.message ?? detail;
        }
      } catch {}
      throw new ApiError(res.status, detail);
    }

    return (await res.json()) as T;
  },

  /** Cunha um token LiveKit para entrar na voz do canal. */
  getToken: async (eventId: string, channelId: string): Promise<EventVoiceToken> =>
    eventVoiceApi._post<EventVoiceToken>('/token', {
      event_id: eventId,
      channel_id: channelId,
    }),

  /**
   * Mute imposto pela comissão (server-side, via SFU). Devolve quantos alvos
   * foram atingidos — útil para avisar "2 de 3 não puderam ser mutados".
   */
  moderate: async (
    eventId: string,
    channelId: string,
    action: VoiceModerationAction,
    targetUserId?: string
  ): Promise<VoiceModerationResult> =>
    eventVoiceApi._post<VoiceModerationResult>('/moderate', {
      event_id: eventId,
      channel_id: channelId,
      action,
      ...(targetUserId ? { target_user_id: targetUserId } : {}),
    }),

  /**
   * Persiste o modo de isolamento do evento. Não exige canal — vale para quem
   * entrar depois. Quem já está na call é avisado por data packet do LiveKit.
   */
  setIsolationMode: async (eventId: string, mode: VoiceIsolationMode): Promise<VoiceModerationResult> =>
    eventVoiceApi._post<VoiceModerationResult>('/moderate', {
      event_id: eventId,
      action: 'set_isolation',
      mode,
    }),
};

// ── Carteira Safety Global ─────────────────────────────────────────────
export const safetyApi = {
  getMyWallet: async () => {
    return apiFetch<{ wallet: any; records: any[] }>('/safety/me');
  },
  getPilotWallet: async (pilotId: string) => {
    return apiFetch<{ wallet: any; records: any[] }>(`/safety/pilots/${pilotId}`);
  },
  getSummary: async () => {
    return apiFetch<any[]>('/safety/summary');
  },
  applyPenalty: async (payload: {
    pilot_id: string;
    points: number;
    source_type: string;
    severity?: string;
    description: string;
    reference_id?: string;
    expires_in_days?: number;
  }) => {
    return apiFetch<any>('/safety/penalties', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  revokePenalty: async (recordId: string) => {
    return apiFetch<any>(`/safety/records/${recordId}/revoke`, {
      method: 'POST',
    });
  },
  resetCycle: async (reason?: string) => {
    return apiFetch<any>('/safety/reset-cycle', {
      method: 'POST',
      body: JSON.stringify({ reason: reason || 'Encerramento de Ciclo Desportivo' }),
    });
  },
};

// ── Agentes (telemetria em segundo plano) ─────────────────────────────
export interface AgentPairingCode {
  code: string;
  expires_at: string;
}

export interface AgentDevice {
  id: string;
  name: string | null;
  agent_version: string | null;
  is_active: boolean | null;
  last_used_at: string | null;
  created_at: string | null;
}

// Release pública dos agentes (repo e-mss-downloads) — GET /agent/version/latest
export interface AgentRelease {
  version: string;
  tag: string;
  release_url: string;
  published_at: string | null;
  downloads: Record<string, string>;
  digests: Record<string, string | null>;
}

export const agentsApi = {
  createPairingCode: () =>
    api.post<AgentPairingCode>('/agent/pairing', {}),
  listDevices: () =>
    api.get<AgentDevice[]>('/agent/devices'),
  revokeDevice: (deviceId: string) =>
    api.delete<void>(`/agent/devices/${deviceId}`),
  latestVersion: () =>
    api.get<AgentRelease>('/agent/version/latest'),
};

// ── Telemetria (viewer) ──────────────────────────────────────────
export interface TelemetryBatchRow {
  seq: number;
  sample_count: number;
  schema_version: number;
  payload: { samples?: any[]; sample_rate_hz?: number | null } | null;
  recorded_at: string | null;
}

/** Registro de melhor volta extraído da telemetria (pendente de validação). */
export interface TelemetryLapRecord {
  id: string;
  session_id: string;
  user_id: string | null;
  event_id: string | null;
  status: 'pending' | 'confirmed' | 'rejected';
  lap_n: number | null;
  best_lap_ms: number;
  s1_ms: number | null;
  s2_ms: number | null;
  s3_ms: number | null;
  lap_count: number | null;
  valid_lap_count: number | null;
  track_name: string | null;
  track_layout: string | null;
  car_name: string | null;
  simulator: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  created_at: string;
  pilot_display_name?: string;
}

// ── Cobertura pré-corrida (painel do organizador — Sprint A) ────

export interface CoverageSession {
  id: string;
  driver_name: string | null;
  track_name: string | null;
  track_layout: string | null;
  session_type: string | null;
  car_name: string | null;
  started_at: string | null;
  finished_at: string | null;
  sample_count: number;
  event_linked: boolean;
  live: boolean;
  last_batch_at: string | null;
  /** Sessão veio de conta vinculada via driver_associations (Sprint C). */
  via_association?: boolean;
}

export interface CoverageRow {
  pilot_id: string;
  display_name: string;
  username: string | null;
  xbox_gamertag: string | null;
  category_name: string | null;
  paired: boolean;
  device_name: string | null;
  agent_version: string | null;
  last_seen_at: string | null;
  agent_active: boolean;
  status: Record<string, any> | null;
  status_at: string | null;
  packets_per_second: number | null;
  session: CoverageSession | null;
  session_live: boolean;
  track_match: boolean | null;
  /** Label da conta associada que está enviando por este inscrito (Sprint C). */
  association_label?: string | null;
}

export interface CoverageExtra {
  user_id: string | null;
  display_name: string;
  username: string | null;
  xbox_gamertag: string | null;
  device_name: string | null;
  agent_version: string | null;
  session: CoverageSession;
  track_match: boolean | null;
  association_id?: string | null;
  associated_pilot_id?: string | null;
  associated_to?: string | null;
}

export interface DriverAssociation {
  id: string;
  event_id: string;
  driver_label: string;
  source_user_id: string | null;
  pilot_id: string;
  gamertag: string | null;
  gamertag_verified: boolean;
  gamertag_note: string | null;
  created_at: string;
}

export interface DriverAssociationResult {
  association_id: string | null;
  created: boolean;
  gamertag: {
    validated: boolean;
    gamertag: string | null;
    profile_updated: boolean;
    message: string | null;
  };
}

export interface EventCoverage {
  event_id: string;
  generated_at: string;
  windows: { agent_s: number; batch_s: number; status_s: number };
  track_names: string[];
  summary: {
    registered: number;
    paired: number;
    agent_active: number;
    session_live: number;
    track_ok: number;
    unregistered_live: number;
  };
  rows: CoverageRow[];
  extras: CoverageExtra[];
}

// ── Rascunho de resultado da telemetria (Sprint D) ───────────────
// Linhas são apenas DADOS pré-preenchidos (editáveis) na grade do
// organizador — nada aqui mexe em eRS/preview/publicação/Discord.

export type ResultDraftStatus = 'registered' | 'dns' | 'dnf' | 'dsq';

export interface ResultDraftRow {
  pilot_id: string;
  display_name: string;
  /** Sessão veio de conta vinculada via driver_associations (Sprint C). */
  via_association: boolean;
  association_labels: string[];
  position: number | null;
  status: ResultDraftStatus;
  laps_completed: number;
  /** "0:49.750" — apenas voltas válidas. */
  best_lap: string;
  /** "15:06.827" — Σ dos tempos oficiais das voltas + span da última. */
  total_time: string;
  /** Mesmo valor de `total_time` em ms (auxiliar de composição/auditoria). */
  total_ms?: number;
  /** Camada 1: ≥3 rodas em rumble strip ≥400ms (kerbMask). */
  off_track_count: number;
  /** Camada 2: excursão além do envelope do corredor ≥400ms. */
  off_track_layer2: number;
  fuel_used_pct: number | null;
  fuel_per_lap_pct: number | null;
  max_tyre_temp_c: number | null;
  max_oil_temp_c: number | null;
  max_water_temp_c: number | null;
  session_id: string | null;
}

export interface ResultDraftStats {
  generated_at: string;
  sessions_used: number;
  sessions_scanned: number;
  samples_total: number;
  leader_laps: number;
  ranking_source: 'game_position' | 'computed';
  /** 'assigned' = sessões atribuídas à corrida da etapa; 'legacy_window' = fallback por horário. */
  assignment_mode?: 'assigned' | 'legacy_window';
  /** Pilotos inscritos sem sessão de corrida atribuída (rascunho parcial). */
  unassigned_count?: number;
  unassigned_pilots?: { pilot_id: string; display_name: string }[];
  offtrack: { layer1: string; layer2: string };
}

export interface ResultDraft {
  event_id: string;
  rows: ResultDraftRow[];
  stats: ResultDraftStats | null;
  generated_at: string;
}

// ── Atribuição: sessão de telemetria → sessão da etapa ──────────
// O conector do Forza reporta toda sessão como 'Practice' (o pacote UDP do
// FM2023 não expõe o tipo), então treino e corrida são indistinguíveis sem o
// organizador confirmar. Ver SessionAssignmentPanel.

export interface EventSessionOption {
  id: string;
  label: string | null;
  session_type: string | null;
  session_number: number | null;
  race_number: number | null;
  /** Início previsto da corrida (UTC), de virtual_start_time + data do dia. */
  race_start: string | null;
  /** Duração prevista da corrida (min) — fecha a janela da captura. */
  duration_minutes: number | null;
}

export interface SessionCandidate {
  id: string;
  user_id: string | null;
  driver_name: string | null;
  track_name: string | null;
  car_name: string | null;
  session_type: string | null;
  started_at: string | null;
  finished_at: string | null;
  sample_count: number | null;
  lap_count: number | null;
  valid_lap_count: number | null;
  best_lap_ms: number | null;
  event_session_id: string | null;
  match_source: string | null;
  /** Desvio em segundos entre o início da sessão e o horário agendado da etapa. */
  time_deviation_s: number | null;
  /** Sessão da etapa sugerida — só quando o piloto tem +1 candidata. */
  suggested_event_session_id: string | null;
}

export interface SessionCandidatesPilot {
  pilot_id: string;
  display_name: string;
  category_name: string | null;
  /** +1 sessão no mesmo padrão → pré-análise comparativa disponível. */
  ambiguous: boolean;
  needs_assignment: boolean;
  sessions: SessionCandidate[];
}

export interface SessionCandidates {
  event_id: string;
  event_sessions: EventSessionOption[];
  scheduled_at: string | null;
  track_names: string[];
  pilots: SessionCandidatesPilot[];
  /**
   * Sessões capturadas de inscritos que o matcher NÃO vinculou (ex.: treino da
   * véspera, antes do alargamento da janela). Mesmo formato de `pilots`, sujeitas
   * aos mesmos filtros do backend — o que aparece aqui já passou por pista,
   * inscrição, janela e pela regra de melhoria. Decidir é ação do organizador.
   * Sempre vazia quando há corrida escolhida (modo lançamento).
   */
  unlinked: SessionCandidatesPilot[];
  summary: {
    registered: number;
    with_sessions: number;
    ambiguous: number;
    assigned: number;
    pending: number;
    /** nº de sessões na seção "sem vínculo". */
    unlinked: number;
  };
}

export interface SessionAssignResult {
  session_id: string;
  event_id: string;
  event_session_id: string | null;
  match_source: string;
}

/**
 * "Chave" de auto-vinculação gerada quando o organizador valida o vínculo no
 * Pit Lane: piloto × liga × carro × pista. Coletas futuras no mesmo padrão já
 * nascem vinculadas (match_source = 'rule').
 */
export interface TelemetryRuleResult {
  ok: boolean;
  created: boolean;
  rule: {
    pilot_id: string;
    league_id: string;
    car_name: string | null;
    track_name: string | null;
  };
}

/** Resultado do recálculo em lote dos registros de melhor volta. */
export interface LapRecordsRecomputeResult {
  scanned: number;
  updated: number;
  rejected: number;
  skipped: number;
}

// ── TRACK (Entrega 1 do arco PRO): perfil + relatório ───────────
export interface TrackDimensionMetric {
  value: number | null;
  target: number | null;
  target_source?: 'rede' | 'ia';
  rel?: number;
}
export interface TrackDimension {
  label: string;
  score: number | null;
  metrics: Record<string, TrackDimensionMetric>;
}
export interface TrackBadge {
  badge: string;
  metric: string;
  value: number;
  limit: number;
  label: string;
}
export interface TrackOpportunity {
  metric: string;
  label: string;
  value: number | null;
  target: number;
  rel: number;
  badge: string | null;
}
export interface TrackBaseline {
  level: string | null;
  label: string;
  n: number;
  metrics: Record<string, number>;
  ladder?: { level: string | null; label: string; n: number }[];
}
export interface TrackProfile {
  dimensions: Record<string, TrackDimension>;
  metrics: Record<string, number | null>;
  badges: TrackBadge[];
  opportunities: TrackOpportunity[];
  plan: string[];
  n_laps: number;
  n_valid_laps: number;
  n_corners: number;
  baseline: TrackBaseline | null;
  info?: Record<string, any>;
}
export interface TrackProfilePayload {
  session: {
    id: string;
    carName: string | null;
    trackName: string | null;
    trackLayout: string | null;
    sessionType: string | null;
    simulator: string | null;
    driverName: string | null;
    startedAt: string | null;
  };
  profile: TrackProfile;
}
export interface TrackReportFile {
  publicUrl: string;
  filePath: string;
  pageCount: number;
  sizeBytes: number;
}
export interface TrackReportItem {
  id: string;
  sessionId: string;
  kind: string;
  publicUrl: string | null;
  pageCount: number | null;
  sizeBytes: number | null;
  createdAt: string | null;
  session: {
    carName: string | null;
    trackName: string | null;
    simulator: string | null;
    startedAt: string | null;
  };
}

// ── Benchmark da etapa (Entrega 2 — Nível 3, degradação justa) ──
export interface TrackBenchmarkMetricSummary {
  label: string;
  unit: string;
  higherIsBetter: boolean;
  n: number;
  avg: number | null;
  best: number | null;
  bestPilotId: string | null;
}
export interface TrackBenchmarkPilot {
  pilotId: string;
  displayName: string;
  nLaps: number;
  metrics: Record<string, number | null>;
}
export interface TrackBenchmarkCondition {
  simulator: string | null;
  trackName: string | null;
  trackLayout: string | null;
}
export interface TrackBenchmarkDifferentCondition extends TrackBenchmarkCondition {
  pilotId: string;
  displayName: string;
}
export interface TrackBenchmark {
  eventId: string;
  gridSize: number;
  withTelemetry: number;
  coveragePct: number;
  level: 'full' | 'partial' | 'coverage_only';
  official: boolean;
  seal: string | null;
  warning: string | null;
  minLaps: number;
  minPilots: number;
  sigmaDisplayLaps: number;
  nPilots: number;
  pilotsReady: number;
  /** Condições da coleta que define o ranking (grupo majoritário). */
  conditions: TrackBenchmarkCondition | null;
  /** Há pilotos com pista/layout/simulador diferentes → sem selo. */
  conditionsMixed: boolean;
  /** "Laguna Seca · AMS2" — rótulo pronto das condições de referência. */
  conditionLabel: string | null;
  /** Quem rodou em outra configuração: listado à parte, fora do ranking. */
  differentConditions: TrackBenchmarkDifferentCondition[];
  /** Taxa medida das amostras (telemetry_batches.sample_rate_hz), em Hz. */
  sampleRateHzMin: number | null;
  sampleRateHzMax: number | null;
  metrics: Record<string, TrackBenchmarkMetricSummary>;
  pilots: TrackBenchmarkPilot[];
  missing: { pilotId: string; displayName: string }[];
}

export const telemetryApi = {
  /** Cobertura pré-corrida (painel do organizador): inscritos × agente × sessão. */
  coverage: (eventId: string) =>
    api.get<EventCoverage>(`/telemetry/events/${eventId}/coverage`),
  /** Sessões de telemetria do evento + sugestão da sessão da etapa (corrida).
   *  `targetSessionId` é a corrida escolhida no lançamento: ela vira o filtro
   *  da busca (só capturas na janela daquela corrida) e a sugestão de todas. */
  sessionCandidates: (eventId: string, targetSessionId?: string) =>
    api.get<SessionCandidates>(
      `/telemetry/events/${eventId}/session-candidates`
      + (targetSessionId ? `?target_session_id=${encodeURIComponent(targetSessionId)}` : ''),
    ),
  /**
   * Define (ou desfaz, com null) a sessão da etapa de uma sessão de telemetria.
   *
   * `eventId` só é usado pelo backend quando a captura ainda está SEM vínculo
   * (seção "sem vínculo"): é a etapa que o organizador declara, validada por
   * staff e pelo pertencimento do destino. Com a sessão já vinculada o campo é
   * ignorado, então é seguro enviá-lo sempre.
   */
  assignSession: (sessionId: string, eventSessionId: string | null, eventId?: string) =>
    api.put<SessionAssignResult>(`/telemetry/sessions/${sessionId}/assign`, {
      event_session_id: eventSessionId,
      event_id: eventId ?? null,
    }),
  /**
   * Gera a chave de auto-vinculação (piloto × liga × carro × pista) a partir da
   * sessão confirmada no Pit Lane — coletas futuras no mesmo padrão já entram
   * vinculadas, sem novo pedido de aprovação.
   */
  createTelemetryRule: (eventId: string, sessionId: string, pilotId?: string) =>
    api.post<TelemetryRuleResult>(`/telemetry/events/${eventId}/telemetry-rules`, {
      session_id: sessionId,
      ...(pilotId ? { pilot_id: pilotId } : {}),
    }),
  /**
   * Recalcula registros de melhor volta com o critério vigente (backfill).
   * Sem isso, um "melhor volta" de 15s gravado antes da correção continua
   * no Pit Lane e na tela de lançamento.
   */
  recomputeLapRecords: (eventId?: string, limit?: number) => {
    const p = new URLSearchParams();
    if (eventId) p.set('event_id', eventId);
    if (limit != null) p.set('limit', String(limit));
    return api.post<LapRecordsRecomputeResult>(
      `/telemetry/lap-records/recompute${p.toString() ? `?${p}` : ''}`,
      {},
    );
  },
  /** Associa identidade de telemetria (label/conta) a um inscrito (Sprint C). */
  associateDriver: (eventId: string, d: {
    pilot_id: string;
    driver_label?: string;
    source_user_id?: string;
    gamertag?: string;
  }) => api.post<DriverAssociationResult>(
    `/telemetry/events/${eventId}/driver-associations`, d),
  listDriverAssociations: (eventId: string) =>
    api.get<{ associations: DriverAssociation[] }>(
      `/telemetry/events/${eventId}/driver-associations`),
  deleteDriverAssociation: (eventId: string, assocId: string) =>
    api.delete<{ ok: boolean }>(
      `/telemetry/events/${eventId}/driver-associations/${assocId}`),
  /** Gera o rascunho de resultado da telemetria e persiste (Sprint D). */
  generateResultsDraft: (eventId: string) =>
    api.post<{ draft: ResultDraft; warning: string | null }>(
      `/telemetry/events/${eventId}/results-draft`),
  /** Lê o rascunho de resultado salvo do evento (null se não há). */
  getResultsDraft: (eventId: string) =>
    api.get<{ draft: ResultDraft | null }>(
      `/telemetry/events/${eventId}/results-draft`),
  listSessions: (limit = 50) =>
    api.get<any[]>(`/telemetry/sessions?limit=${limit}`),
  getBatches: (sessionId: string, refSessionId?: string) =>
    api.get<TelemetryBatchRow[]>(
      `/telemetry/sessions/${sessionId}/batches${refSessionId ? `?ref_session_id=${refSessionId}` : ''}`),
  /** View analisada no servidor (Melhor/OPT/Pace + resumo) — baixa pouco. */
  getView: (sessionId: string, laps?: number[], refSessionId?: string) => {
    const q = new URLSearchParams();
    if (laps?.length) q.set('laps', laps.join(','));
    if (refSessionId) q.set('ref_session_id', refSessionId);
    const s = q.toString();
    return api.get<TelemetrySessionViewPayload>(
      `/telemetry/sessions/${sessionId}/view${s ? `?${s}` : ''}`);
  },
  browseSessions: (track: string, limit = 20, refSessionId?: string) =>
    api.get<any[]>(
      `/telemetry/browse?track=${encodeURIComponent(track)}&limit=${limit}${refSessionId ? `&ref_session_id=${refSessionId}` : ''}`),
  rematch: () =>
    api.post<{ linked: number }>('/telemetry/rematch'),
  /** Registros de melhor volta pendentes (event_id = evento; sem event_id = admin). */
  listLapRecords: (params: { eventId?: string; status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.eventId) q.append('event_id', params.eventId);
    if (params.status) q.append('status', params.status);
    return api.get<TelemetryLapRecord[]>(`/telemetry/lap-records${q.toString() ? `?${q}` : ''}`);
  },
  /** Valida o vínculo ao evento e integra o tempo em session_laps. */
  confirmLapRecord: (id: string, eventId?: string) =>
    api.post<{ ok: boolean; integrated: boolean }>(
      `/telemetry/lap-records/${id}/confirm`, eventId ? { event_id: eventId } : {}),
  rejectLapRecord: (id: string) =>
    api.post<{ ok: boolean }>(`/telemetry/lap-records/${id}/reject`, {}),
  /** Perfil TRACK da sessão (4 dimensões + baseline) — prévia gratuita do dono. */
  getTrackProfile: (sessionId: string) =>
    api.get<TrackProfilePayload>(`/telemetry/sessions/${sessionId}/track-profile`),
  /** Gera o PDF do relatório TRACK (Piloto PRO; 403 para free). */
  generateTrackReport: (sessionId: string) =>
    api.post<TrackReportFile>(`/telemetry/sessions/${sessionId}/report`, {}),
  /** Histórico de relatórios TRACK do piloto autenticado. */
  listTrackReports: (limit = 50) =>
    api.get<TrackReportItem[]>(`/telemetry/reports?limit=${limit}`),
};

// ── View analisada (endpoint /view) ─────────────────────────────
export interface TelemetryLapPayload {
  id: string;
  n: number;
  time: number;
  valid: boolean;
  complete: boolean;
  sec: number[];
  synthetic: 'opt' | 'pace' | null;
  best: boolean;
  /** Canais reamostrados na grade de 2 m; null = NaN. */
  g: Record<string, (number | null)[]>;
}

export interface TelemetrySessionViewPayload {
  L: number;
  N: number;
  gx: number[];
  bounds: { x0: number; x1: number; y0: number; y1: number };
  laps: TelemetryLapPayload[];
  extra_laps?: TelemetryLapPayload[];
  summaries: {
    n: number; time: number; sec: number[];
    valid: boolean; complete: boolean;
  }[];
  meta: Record<string, string | null>;
}

// Vínculo telemetria ↔ evento (staff/dono)
export interface EventTelemetrySession {
  id: string;
  user_id: string;
  driver_name: string | null;
  pilot_display_name: string;
  car_name: string | null;
  track_name: string | null;
  track_layout: string | null;
  session_type: string | null;
  simulator: string | null;
  started_at: string | null;
  finished_at: string | null;
  sample_count: number | null;
  match_source: string | null;
  /** Sessão oficial da etapa a que pertence (null = ainda não atribuída). */
  event_session_id: string | null;
}

export const eventTelemetryApi = {
  /**
   * Sessões de telemetria do evento. Por padrão exclui as que o organizador
   * atribuiu a treino/quali — sem isso o Pit Lane misturava treino com corrida.
   */
  listSessions: (eventId: string, includeNonRace = false) =>
    api.get<EventTelemetrySession[]>(
      `/events/${eventId}/telemetry-sessions?include_non_race=${includeNonRace}`),
  getBatches: (eventId: string, sessionId: string) =>
    api.get<TelemetryBatchRow[]>(`/events/${eventId}/telemetry/${sessionId}/batches`),
};

// ── Monetização & Billing (Vercel Serverless TS /api/billing/*) ────────
export interface SubscriptionPlanItem {
  id: string;
  name: string;
  slug: string;
  role_slug: string;
  tier: 'free' | 'paid';
  price_brl_cents: number;
  billing_period: 'monthly' | 'annual' | 'event';
  is_active: boolean;
  sort_order: number;
}

export interface ManualPaymentItem {
  id: string;
  ref: string;
  amount_brl_cents: number;
  original_brl_cents: number;
  discount_percent: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  proof_text?: string | null;
  proof_url?: string | null;
  reject_reason?: string | null;
  period_end?: string | null;
  approved_at?: string | null;
  created_at: string;
  users?: {
    id: string;
    username: string;
    display_name: string;
    email?: string;
    avatar_url?: string | null;
  };
  subscription_plans?: SubscriptionPlanItem;
  discount_vouchers?: {
    id: string;
    code: string;
    percent: number;
    note?: string | null;
  };
}

export interface DiscountVoucherItem {
  id: string;
  code: string;
  percent: number;
  scope: 'all' | 'pilot' | 'liga';
  max_uses: number;
  used_count: number;
  expires_at: string;
  active: boolean;
  note?: string | null;
  created_at: string;
}

export interface BillingUserStatusPayload {
  subscriptions: any[];
  user_roles: any[];
  recent_payments: ManualPaymentItem[];
  leagues: { id: string; name: string; slug: string; access_level: number }[];
  flags: {
    has_pro_pilot: boolean;
    has_organizer_paid: boolean;
  };
}

async function billingFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const vercelUrl = `/api/billing${endpoint}`;
  try {
    const res = await fetch(vercelUrl, { ...options, headers, credentials: 'include' });
    const contentType = res.headers.get('content-type') || '';

    if (res.ok) {
      if (res.status === 204) return undefined as unknown as T;
      if (!contentType.includes('application/json')) {
        throw new ApiError(500, 'Serviço de billing temporariamente indisponível.');
      }
      return await res.json();
    }

    let detail = `HTTP ${res.status}`;
    try {
      if (contentType.includes('application/json')) {
        const json = await res.json();
        detail = json.detail ?? json.message ?? json.error ?? detail;
      }
    } catch {}
    throw new ApiError(res.status, detail);
  } catch (err: any) {
    if (err instanceof ApiError) throw err;
    console.error(`[billingFetch] Falha para ${vercelUrl}:`, err?.message || err);
    throw new ApiError(500, err?.message || 'Erro de conexão com o serviço de assinaturas.');
  }
}

export const billingApi = {
  getPlans: () =>
    billingFetch<SubscriptionPlanItem[]>('/plans'),

  validateVoucher: (code: string, plan_slug?: string) =>
    billingFetch<{
      valid: boolean;
      voucher_id: string;
      code: string;
      percent: number;
      scope: string;
      expires_at: string;
      original_brl_cents: number;
      amount_brl_cents: number;
      requires_admin_review: boolean;
    }>('/voucher', {
      method: 'POST',
      body: JSON.stringify({ code, plan_slug }),
    }),

  requestSubscription: (plan_slug: string, voucher_code?: string, proof_text?: string) =>
    billingFetch<{
      payment_id: string;
      ref: string;
      plan_name: string;
      plan_slug: string;
      billing_period: string;
      amount_brl_cents: number;
      original_brl_cents: number;
      discount_percent: number;
      status: string;
      created_at: string;
      support_username: string | null;
      instruction: string;
    }>('/request', {
      method: 'POST',
      body: JSON.stringify({ plan_slug, voucher_code, proof_text }),
    }),

  // Envia o comprovante (base64) ao serverless — o upload no bucket é
  // server-side: o supabase-js do navegador não tem sessão (login via
  // cookies HttpOnly do backend) e o RLS negaria um upload direto.
  attachProof: (payment_id: string, file_base64: string, file_name: string, proof_text?: string) =>
    billingFetch<{ success: boolean; payment_id: string; ref: string; proof_url: string }>(
      '/attach-proof',
      {
        method: 'POST',
        body: JSON.stringify({ payment_id, file_base64, file_name, proof_text }),
      }
    ),

  getStatus: () =>
    billingFetch<BillingUserStatusPayload>('/status'),

  getAdminQueue: (status?: string) =>
    billingFetch<ManualPaymentItem[]>(`/queue${status ? `?status=${status}` : ''}`),

  approvePayment: (payment_id: string, months?: number) =>
    billingFetch<{
      message: string;
      payment_id: string;
      period_end: string;
      role_slug: string;
      leagues_updated: number;
    }>('/approve', {
      method: 'POST',
      body: JSON.stringify({ payment_id, months }),
    }),

  rejectPayment: (payment_id: string, reason?: string) =>
    billingFetch<{ message: string; payment_id: string }>('/reject', {
      method: 'POST',
      body: JSON.stringify({ payment_id, reason }),
    }),

  assignPlan: (target_user_id: string, plan_slug: string, months?: number, note?: string) =>
    billingFetch<{
      message: string;
      target_user_id: string;
      period_end: string;
      role_slug: string;
      leagues_updated: number;
    }>('/assign', {
      method: 'POST',
      body: JSON.stringify({ target_user_id, plan_slug, months, note }),
    }),

  getVouchers: () =>
    billingFetch<DiscountVoucherItem[]>('/vouchers'),

  createVouchers: (payload: {
    percent: number;
    scope?: 'all' | 'pilot' | 'liga';
    count?: number;
    days?: number;
    max_uses?: number;
    note?: string;
  }) =>
    billingFetch<{ message: string; vouchers: DiscountVoucherItem[] }>('/vouchers', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};




